/** AutoCAD's own ribbon, menus and command names, transcribed — the data behind AutoCAD mode.
 *
 * ⚠⚠ **THIS FILE IS A TRANSCRIPTION, NOT A DESIGN.** Every label, every command name, every tab
 * and panel order here is what AutoCAD 2024 (Drafting & Annotation) puts on screen. The whole
 * value of the mode is that a drafter with fifteen years in AutoCAD does not have to learn
 * anything — so RECTANG is not renamed "Rectangle tool", the Modify panel is not reordered into
 * something tidier, and the Clipboard panel does not lose the six commands we cannot do.
 * Improving on it defeats it.
 *
 * ⚠⚠ **NOTHING HERE SAYS WHETHER A COMMAND WORKS.** An item is live exactly when `handlers.ts`
 * gives it something to do, and grey otherwise — see `AutoCadShell`. That is deliberate: a
 * hand-kept "implemented" flag in this file would drift out of step with the wiring the first
 * time a tool landed, and the failure mode is the worst one there is — a button that looks live
 * and silently does nothing. Deleting a handler greys the item in the same edit.
 *
 * ⚠⚠ **THE `cmd` FIELD IS LOAD-BEARING, NOT A LABEL.** It is what you can type at the `Command:`
 * prompt, and the command line resolves against THIS table — so the ribbon button, the pull-down
 * item and the typed command are one id with one handler and cannot disagree. A command line
 * that ran things the ribbon greys out would be two implementations of the same product.
 *
 * ⚠ **English labels, on purpose, in a trilingual app.** Autodesk ships no Vietnamese AutoCAD,
 * so every Vietnamese drafter who knows this product knows it as TRIM, OFFSET, MLEADER — and
 * types those letters. Translating the menu would make the mode LESS familiar, which is the one
 * thing it exists to be. The rest of the app stays VN/EN/中文; the drafting chrome is the
 * exception, and deliberately so.
 */

// ── the shapes ───────────────────────────────────────────────────────────────────────────────

/** A control that is not a button: the ribbon's Layer picker, the underlay's Fade slider. */
// ⚠ The one import this file has. `catalog.ts` is otherwise pure transcription, and it stays
// that way — `geometry.ts` is data and functions with no React and no canvas behind it, so
// taking PDMODE from there costs the eager chunk nothing. Importing it from `PlanStudio` would
// drag the whole lazy-loaded studio into first paint.
import { PDMODES } from "./acadtables";

export type AcadControl = "select" | "slider";

export interface AcadBtn {
  /** The command id — the key `handlers.ts` answers to. Shared wherever the command appears. */
  id: string;
  /** As AutoCAD prints it under a large button, or in a menu. */
  label: string;
  /** What you type at `Command:`. Absent on things that are not commands. */
  cmd?: string;
  /** The shortcut AutoCAD prints down the right of a pull-down. */
  key?: string;
  /** The line AutoCAD's rich tooltip prints under the name. */
  hint?: string;
  /** Drawn at 32 px with the label beneath, as AutoCAD draws a panel's leading command. */
  big?: boolean;
  /** A split button's drop-down. The parent stays clickable; the arrow opens this. */
  flyout?: AcadBtn[];
  /** Renders a live control instead of a button — see `AcadHandler.select` / `.slider`. */
  control?: AcadControl;
  /** How wide a `control` sits in the panel. */
  width?: number;
}

export type AcadGroup =
  | { big: AcadBtn }               // one large button with its label under it
  | { col: AcadBtn[] }             // up to three small buttons stacked, AutoCAD's usual column
  | { wide: AcadBtn }              // a full-width control — the Layer picker and friends
  | { stack: AcadBtn[] }           // controls stacked VERTICALLY — Color over Linetype over…
  | { gap: true };                 // the thin rule AutoCAD draws between groups

export interface AcadPanel {
  id: string;
  label: string;
  groups: AcadGroup[];
  /** The ⌄ at the right of the panel title — AutoCAD's dialog launcher. */
  launcher?: AcadBtn;
  /** What drops when the panel title's ▾ is clicked. AutoCAD calls this the slide-out. */
  more?: AcadGroup[];
}

export interface AcadTab { id: string; label: string; panels: AcadPanel[] }

/** How wide a panel will be, computed from its own groups.
 *
 * ⚠⚠ **ARITHMETIC, NOT MEASUREMENT, AND THAT IS THE PERFORMANCE DECISION.** AutoCAD collapses
 * trailing panels to a button when the ribbon does not fit, and the obvious way to know when is
 * to render everything and measure it — which means a layout pass, a ResizeObserver per panel
 * and a second render on every tab change. Every group here has a FIXED width (a large button is
 * 62, a column of small ones 26, a control its own width), so the answer is a sum over static
 * data: no DOM read, no reflow, and the same number every time. */
// ⚠ CALIBRATED AGAINST THE REAL THING, not guessed: measured in a browser at 2 400 px where
// nothing folds, then tuned until every estimate is >= the measured width. Erring HIGH is the
// safe direction — it folds a panel one step early, where erring low clips a panel off the
// screen edge, which is the failure being fixed. Measured/estimated, Home tab: draw 304/301,
// modify 148/148, annotation 267/266, layers 322/331, block 127/135, properties 309/318.
const CTL_CHROME = 27;          // a control's icon, its gap and the row's own padding
const BTN_W = 29;               // a small icon-only button
const BTN_FLY_W = 35;           // …with a split-button arrow beside it
const BIG_W = 63;
const btnWidth = (b: AcadBtn): number =>
  b.control ? (b.width || 150) + CTL_CHROME : (b.flyout ? BTN_FLY_W : BTN_W);

const groupWidth = (g: AcadGroup): number => {
  if ("gap" in g) return 9;
  if ("big" in g) return BIG_W;
  if ("wide" in g) return (g.wide.width || 150) + CTL_CHROME;
  if ("stack" in g) return Math.max(...g.stack.map((b) => (b.width || 150) + CTL_CHROME));
  return Math.max(...g.col.map(btnWidth));
};

export const panelWidth = (p: AcadPanel): number =>
  p.groups.reduce((a, g) => a + groupWidth(g), 0) + 14;

/** A panel collapsed to a single button, as AutoCAD draws it when the ribbon runs out of room. */
export const FOLDED_W = 66;

export interface AcadMenuItem {
  id?: string;
  label?: string;
  key?: string;
  sep?: boolean;
  sub?: AcadMenuItem[];
  /** Drawn with a tick when on. */
  toggle?: boolean;
  /** Drawn with a bullet when on — one of a set. */
  radio?: boolean;
}

export interface AcadMenu { label: string; items: AcadMenuItem[] }

const S: AcadMenuItem = { sep: true };

// ── the ribbon ───────────────────────────────────────────────────────────────────────────────

/** Home — the tab a drafter never leaves. Ten panels, in AutoCAD's own order.
 *
 * ⚠ A split button's TOP HALF is the same id as one row of its flyout, never a second id. In
 * AutoCAD the top half re-runs whichever variant you used last; here it runs the one we have.
 * Two ids for one command is how a toolbar button ends up wired and its menu item does not.
 */
const HOME: AcadPanel = {
  id: "p.draw", label: "Draw",
  groups: [
    { big: { id: "draw.line", label: "Line", cmd: "LINE", key: "L", big: true,
             hint: "Draws straight segments. Type a length and press Enter for an exact one." } },
    { big: { id: "draw.pline", label: "Polyline", cmd: "PLINE", key: "PL", big: true,
             hint: "Draws a connected run of segments as one object." } },
    { big: { id: "draw.circle", label: "Circle", cmd: "CIRCLE", key: "C", big: true,
             hint: "Centre, then radius. Type the radius for an exact one.",
             flyout: [
               { id: "draw.circle", label: "Center, Radius", cmd: "CIRCLE" },
               { id: "draw.circle.cd", label: "Center, Diameter" },
               { id: "draw.circle.2p", label: "2-Point" },
               { id: "draw.circle.3p", label: "3-Point" },
               { id: "draw.circle.ttr", label: "Tan, Tan, Radius" },
               { id: "draw.circle.ttt", label: "Tan, Tan, Tan" },
             ] } },
    // ⚠ The ARC command is live and every NAMED variant under it is grey, which looks odd until
    // you see why: ours is start → end → a point the arc passes through, and each of AutoCAD's
    // eleven names promises a specific pick ORDER we do not have. A drafter reaching for
    // "Start, Center, End" and getting our gesture would place the arc somewhere else and find
    // out later. The plain command keeps its own honest prompt; the promises stay unmade.
    { big: { id: "draw.arc", label: "Arc", cmd: "ARC", key: "A", big: true,
             hint: "Start point, end point, then a point the arc passes through.",
             flyout: [
               { id: "draw.arc.3p", label: "3-Point" },
               { id: "draw.arc.sce", label: "Start, Center, End" },
               { id: "draw.arc.sca", label: "Start, Center, Angle" },
               { id: "draw.arc.scl", label: "Start, Center, Length" },
               { id: "draw.arc.sea", label: "Start, End, Angle" },
               { id: "draw.arc.sed", label: "Start, End, Direction" },
               { id: "draw.arc.ser", label: "Start, End, Radius" },
               { id: "draw.arc.cse", label: "Center, Start, End" },
               { id: "draw.arc.csa", label: "Center, Start, Angle" },
               { id: "draw.arc.csl", label: "Center, Start, Length" },
               { id: "draw.arc.cont", label: "Continue" },
             ] } },
    { col: [
      { id: "draw.rect", label: "Rectangle", cmd: "RECTANG", key: "REC",
        hint: "First corner, then the opposite one.",
        flyout: [
          { id: "draw.rect", label: "Rectangle", cmd: "RECTANG" },
          { id: "draw.polygon", label: "Polygon", cmd: "POLYGON", key: "POL" },
        ] },
      { id: "draw.ellipse.axis", label: "Ellipse", cmd: "ELLIPSE",
        flyout: [
          { id: "draw.ellipse.axis", label: "Axis, End", cmd: "ELLIPSE" },
          { id: "draw.ellipse.center", label: "Center" },
          { id: "draw.ellipse.arc", label: "Elliptical Arc" },
        ] },
      { id: "draw.hatch", label: "Hatch", cmd: "HATCH", key: "H",
        hint: "Click inside a closed region to pour the drawing's current material into it.",
        flyout: [
          { id: "draw.hatch", label: "Hatch", cmd: "HATCH" },
          { id: "draw.gradient", label: "Gradient", cmd: "GRADIENT" },
          { id: "draw.boundary", label: "Boundary", cmd: "BOUNDARY" },
          { id: "draw.region", label: "Region", cmd: "REGION" },
        ] },
    ] },
  ],
  more: [
    { col: [
      { id: "draw.xline", label: "Construction Line", cmd: "XLINE", key: "XL",
        hint: "An unbounded guideline to draw against." },
      { id: "draw.ray", label: "Ray", cmd: "RAY" },
      { id: "draw.mline", label: "Multiline", cmd: "MLINE" },
    ] },
    { col: [
      { id: "draw.spline.fit", label: "Spline Fit", cmd: "SPLINE", key: "SPL" },
      { id: "draw.spline.cv", label: "Spline CV", cmd: "SPLINECV" },
      { id: "draw.revcloud", label: "Revision Cloud", cmd: "REVCLOUD",
        flyout: [
          { id: "draw.revcloud.rect", label: "Rectangular" },
          { id: "draw.revcloud", label: "Polygonal", cmd: "REVCLOUD" },
          { id: "draw.revcloud.free", label: "Freehand" },
        ] },
    ] },
    { col: [
      { id: "draw.donut", label: "Donut", cmd: "DONUT" },
      { id: "draw.wipeout", label: "Wipeout", cmd: "WIPEOUT" },
      { id: "draw.3dpoly", label: "3D Polyline", cmd: "3DPOLY" },
    ] },
    { col: [
      { id: "draw.helix", label: "Helix", cmd: "HELIX" },
      { id: "draw.point", label: "Multiple Points", cmd: "POINT",
        flyout: [{ id: "draw.point", label: "Multiple Points" },
                 ...PDMODES.map((m) => ({ id: `pdmode.${m.mode}`, label: `Style: ${m.label}` }))] },
      { id: "draw.pointstyle", label: "Point Style...", cmd: "DDPTYPE" },
    ] },
  ],
};

const MODIFY: AcadPanel = {
  id: "p.modify", label: "Modify",
  groups: [
    { col: [
      { id: "mod.move", label: "Move", cmd: "MOVE", key: "M",
        hint: "Moves the selection by the distance on the command bar." },
      { id: "mod.rotate", label: "Rotate", cmd: "ROTATE", key: "RO",
        hint: "Turns the selection about its own centre by the angle on the command bar." },
      { id: "mod.trim", label: "Trim", cmd: "TRIM", key: "TR",
        hint: "Select what to trim, then click the piece to cut away.",
        flyout: [
          { id: "mod.trim", label: "Trim", cmd: "TRIM" },
          { id: "mod.extend", label: "Extend", cmd: "EXTEND", key: "EX" },
        ] },
    ] },
    { col: [
      // ⚠⚠ AutoCAD MEANS TWO DIFFERENT THINGS BY "COPY" AND WE HONOUR BOTH. This one is the
      // Modify command — duplicate at a displacement — which is exactly our `copy` edit-op, so
      // it is live. Ctrl+C is COPYCLIP, a clipboard we do not have, and it stays grey on the
      // Clipboard panel. Wiring them to each other would give a drafter a Ctrl+C that quietly
      // drew a second wall 200 mm to the right.
      { id: "mod.copy", label: "Copy", cmd: "COPY", key: "CO",
        hint: "Duplicates the selection at an offset. Not the clipboard — that is COPYCLIP." },
      { id: "mod.mirror", label: "Mirror", cmd: "MIRROR", key: "MI",
        hint: "Pick the two points of the mirror line." },
      { id: "mod.fillet", label: "Fillet", cmd: "FILLET", key: "F",
        hint: "Rounds the corner to the radius on the command bar.",
        flyout: [
          { id: "mod.fillet", label: "Fillet", cmd: "FILLET" },
          { id: "mod.chamfer", label: "Chamfer", cmd: "CHAMFER", key: "CHA" },
          { id: "mod.blend", label: "Blend Curves", cmd: "BLEND" },
        ] },
    ] },
    { col: [
      { id: "mod.stretch", label: "Stretch", cmd: "STRETCH", key: "S" },
      { id: "mod.scale", label: "Scale", cmd: "SCALE", key: "SC",
        hint: "Scales the selection about its own centre." },
      { id: "mod.array.rect", label: "Array", cmd: "ARRAYRECT", key: "AR",
        hint: "Repeats the selection in a row.",
        flyout: [
          { id: "mod.array.rect", label: "Rectangular Array", cmd: "ARRAYRECT" },
          { id: "mod.array.path", label: "Path Array", cmd: "ARRAYPATH" },
          { id: "mod.array.polar", label: "Polar Array", cmd: "ARRAYPOLAR" },
        ] },
    ] },
    { col: [
      { id: "mod.offset", label: "Offset", cmd: "OFFSET", key: "O",
        hint: "Draws a parallel copy at the distance on the command bar." },
      { id: "mod.explode", label: "Explode", cmd: "EXPLODE", key: "X" },
      { id: "mod.erase", label: "Erase", cmd: "ERASE", key: "E",
        hint: "Deletes the selection." },
    ] },
  ],
  more: [
    { col: [
      { id: "mod.extend", label: "Extend", cmd: "EXTEND", key: "EX" },
      { id: "mod.break", label: "Break", cmd: "BREAK", key: "BR" },
      { id: "mod.breakat", label: "Break at Point", cmd: "BREAKATPOINT" },
    ] },
    { col: [
      { id: "mod.join", label: "Join", cmd: "JOIN", key: "J" },
      { id: "mod.align", label: "Align", cmd: "ALIGN", key: "AL" },
      { id: "mod.reverse", label: "Reverse", cmd: "REVERSE" },
    ] },
    { col: [
      { id: "mod.pedit", label: "Edit Polyline", cmd: "PEDIT",
        flyout: [
          { id: "mod.pedit.close", label: "Close" },
          { id: "mod.pedit.open", label: "Open" },
          { id: "mod.pedit.width", label: "Width — all segments" },
          { id: "mod.pedit.addvertex", label: "Add vertex" },
          { id: "mod.pedit.delvertex", label: "Delete vertex" },
          { id: "mod.pedit.arc", label: "Arc — bend a segment" },
          { id: "mod.pedit.straight", label: "Straighten a segment" },
          // ⚠ FIT and DECURVE stay listed and stay GREY. They replace the polyline's geometry
          // with a curve approximation — a different object with a different vertex count — and
          // the server names them in `PEDIT_NOT_BUILT` so picking one says exactly that
          // instead of reporting success and changing nothing.
          { id: "mod.pedit.fit", label: "Fit — arcs through the vertices" },
          { id: "mod.pedit.decurve", label: "Decurve — back to straight" },
          // ⚠ AutoCAD's PEDIT also offers Join and Reverse. Both are their own commands in this
          // shell and both are BUILT, so the flyout points at them rather than duplicating the
          // wiring — two paths to one behaviour is two places for it to drift.
          { id: "mod.join", label: "Join" },
          { id: "mod.reverse", label: "Reverse" },
        ] },
      { id: "mod.lengthen", label: "Lengthen", cmd: "LENGTHEN" },
      { id: "mod.divide", label: "Divide", cmd: "DIVIDE", key: "DIV" },
    ] },
    { col: [
      { id: "mod.setbylayer", label: "Set to ByLayer", cmd: "SETBYLAYER" },
      { id: "mod.chspace", label: "Change Space", cmd: "CHSPACE" },
      { id: "mod.overkill", label: "Delete Duplicate Objects", cmd: "OVERKILL" },
    ] },
  ],
};

const ANNOTATION: AcadPanel = {
  id: "p.anno", label: "Annotation",
  groups: [
    { big: { id: "anno.text.single", label: "Text", cmd: "TEXT", key: "DT", big: true,
             hint: "Click where the text goes and type it.",
             flyout: [
               { id: "anno.text.multi", label: "Multiline Text", cmd: "MTEXT", key: "T" },
               { id: "anno.text.single", label: "Single Line Text", cmd: "TEXT", key: "DT" },
             ] } },
    // ⚠⚠ ALIGNED, NOT LINEAR, AND THE DIFFERENCE IS A WRONG NUMBER ON A DRAWING. `dimShape`
    // puts the dimension line at `p1 + off` → `p2 + off` — always PARALLEL to the two points —
    // and labels it `dist(p1, p2)`, the true distance. That is DIMALIGNED. AutoCAD's DIMLINEAR
    // measures the horizontal or vertical PROJECTION instead, so on a sloped wall the two give
    // different numbers. Wiring ours to the DIMLINEAR button would hand a drafter the slope
    // length under a label promising the run, and it would be found on site.
    { big: { id: "anno.dim.aligned", label: "Dimension", cmd: "DIMALIGNED", key: "DAL", big: true,
             hint: "Pick two points, then pull the dimension line clear. Measures the true "
                 + "distance, parallel to the points.",
             flyout: [
               { id: "anno.dim.linear", label: "Linear", cmd: "DIMLINEAR" },
               { id: "anno.dim.aligned", label: "Aligned", cmd: "DIMALIGNED" },
               { id: "anno.dim.angular", label: "Angular", cmd: "DIMANGULAR" },
               { id: "anno.dim.arc", label: "Arc Length", cmd: "DIMARC" },
               { id: "anno.dim.radius", label: "Radius", cmd: "DIMRADIUS" },
               { id: "anno.dim.diameter", label: "Diameter", cmd: "DIMDIAMETER" },
               { id: "anno.dim.jogged", label: "Jogged", cmd: "DIMJOGGED" },
               { id: "anno.dim.ordinate", label: "Ordinate", cmd: "DIMORDINATE" },
               { id: "anno.dim.continue", label: "Continue", cmd: "DIMCONTINUE" },
               { id: "anno.dim.baseline", label: "Baseline", cmd: "DIMBASELINE" },
               { id: "anno.dim.quick", label: "Quick", cmd: "QDIM" },
             ] } },
    { big: { id: "anno.leader", label: "Leader", cmd: "MLEADER", key: "MLE", big: true,
             hint: "Point at the thing, set the elbow, then set where the note sits.",
             flyout: [
               { id: "anno.leader", label: "Multileader", cmd: "MLEADER" },
               { id: "anno.leader.add", label: "Add Leader", cmd: "MLEADEREDIT" },
               { id: "anno.leader.remove", label: "Remove Leader" },
               { id: "anno.leader.align", label: "Align", cmd: "MLEADERALIGN" },
               { id: "anno.leader.collect", label: "Collect", cmd: "MLEADERCOLLECT" },
             ] } },
    { big: { id: "anno.table", label: "Table", cmd: "TABLE", big: true } },
  ],
  more: [
    { col: [
      { id: "anno.style.text", label: "Text Style...", cmd: "STYLE", key: "ST" },
      { id: "anno.style.dim", label: "Dimension Style...", cmd: "DIMSTYLE", key: "D" },
      { id: "anno.style.mleader", label: "Multileader Style...", cmd: "MLEADERSTYLE" },
    ] },
    { col: [
      { id: "anno.style.table", label: "Table Style...", cmd: "TABLESTYLE" },
      { id: "anno.centermark", label: "Center Mark", cmd: "CENTERMARK" },
      { id: "anno.centerline", label: "Centerline", cmd: "CENTERLINE" },
    ] },
  ],
};

const LAYERS: AcadPanel = {
  id: "p.layers", label: "Layers",
  groups: [
    { big: { id: "layer.props", label: "Layer\nProperties", cmd: "LAYER", key: "LA", big: true,
             hint: "Opens the Layer Properties Manager." } },
    { wide: { id: "layer.control", label: "Layer", control: "select", width: 140,
              hint: "The layer new geometry is drawn on." } },
    { col: [
      { id: "layer.makecurrent", label: "Make Object's Layer Current", cmd: "LAYMCUR" },
      { id: "layer.previous", label: "Previous", cmd: "LAYERP" },
      { id: "layer.match", label: "Match", cmd: "LAYMCH" },
    ] },
    { col: [
      { id: "layer.iso", label: "Isolate", cmd: "LAYISO" },
      { id: "layer.unisolate", label: "Unisolate", cmd: "LAYUNISO" },
      { id: "layer.freeze", label: "Freeze", cmd: "LAYFRZ" },
    ] },
    { col: [
      { id: "layer.off", label: "Off", cmd: "LAYOFF" },
      { id: "layer.on", label: "Turn All Layers On", cmd: "LAYON" },
      { id: "layer.lock", label: "Lock", cmd: "LAYLCK" },
    ] },
  ],
  more: [
    { col: [
      { id: "layer.unlock", label: "Unlock", cmd: "LAYULK" },
      { id: "layer.tocurrent", label: "Change to Current Layer", cmd: "LAYCUR" },
      { id: "layer.copyto", label: "Copy Objects to New Layer", cmd: "COPYTOLAYER" },
    ] },
    { col: [
      { id: "layer.walk", label: "Layer Walk...", cmd: "LAYWALK" },
      { id: "layer.merge", label: "Merge", cmd: "LAYMRG" },
      { id: "layer.delete", label: "Delete", cmd: "LAYDEL" },
    ] },
    { col: [
      { id: "layer.states", label: "Layer States Manager...", cmd: "LAYERSTATE" },
    ] },
  ],
};

const BLOCK: AcadPanel = {
  id: "p.block", label: "Block",
  groups: [
    { big: { id: "block.insert", label: "Insert", cmd: "INSERT", key: "I", big: true,
             hint: "Places a block from the library. Pick one on the Blocks palette first.",
             flyout: [
               { id: "block.insert", label: "Blocks from Libraries...", cmd: "INSERT" },
               { id: "block.recent", label: "Recent Blocks" },
               { id: "block.favourites", label: "Favorite Blocks" },
             ] } },
    { col: [
      { id: "block.create", label: "Create Block", cmd: "BLOCK", key: "B" },
      { id: "block.editor", label: "Block Editor", cmd: "BEDIT", key: "BE" },
      { id: "block.attdef", label: "Define Attributes", cmd: "ATTDEF" },
    ] },
    { col: [
      { id: "block.edit", label: "Edit Attribute", cmd: "EATTEDIT" },
      { id: "block.attsync", label: "Synchronize", cmd: "ATTSYNC" },
      { id: "block.setbase", label: "Set Base Point", cmd: "BASE" },
    ] },
  ],
};

const PROPERTIES: AcadPanel = {
  id: "p.props", label: "Properties",
  // ⚠ The dialog launcher on this panel IS Ctrl+1 in AutoCAD, and it is one of the few things
  // on the panel that works here — the Properties palette is our inspector, unchanged.
  launcher: { id: "prop.palette", label: "Properties", cmd: "PROPERTIES", key: "Ctrl+1" },
  // ⚠ STACKED, because AutoCAD stacks them — and laying the three across cost this panel
  // 727 px, which pushed Groups, Utilities, Clipboard and View off a 1800 px screen entirely.
  groups: [
    { stack: [
      { id: "prop.colour", label: "Color", control: "select", width: 118 },
      { id: "prop.lineweight", label: "Lineweight", control: "select", width: 118 },
      { id: "prop.linetype", label: "Linetype", control: "select", width: 118 },
    ] },
    { col: [
      { id: "prop.match", label: "Match Properties", cmd: "MATCHPROP", key: "MA" },
      { id: "prop.list", label: "List", cmd: "LIST", key: "LI" },
      { id: "prop.transparency", label: "Transparency", control: "select", width: 118 },
    ] },
  ],
};

const GROUPS_PANEL: AcadPanel = {
  id: "p.groups", label: "Groups",
  groups: [
    { big: { id: "group.create", label: "Group", cmd: "GROUP", key: "G", big: true } },
    { col: [
      { id: "group.ungroup", label: "Ungroup", cmd: "UNGROUP" },
      { id: "group.edit", label: "Group Edit", cmd: "GROUPEDIT" },
      { id: "group.selection", label: "Group Selection On/Off", cmd: "PICKSTYLE" },
    ] },
  ],
};

const UTILITIES: AcadPanel = {
  id: "p.util", label: "Utilities",
  groups: [
    // ⚠⚠⚠ **THE BIG BUTTON AND ITS FIRST FLYOUT ENTRY SHARED AN ID, AND `DIST` LOST ITS NAME
    // TO IT.** `_INDEX.out[b.id] ||=` keeps the FIRST declaration, so `util.measure.dist` was
    // registered with `cmd: "MEASUREGEOM"` and the `cmd: "DIST"` below it never reached the
    // index — so `DIST` was in no command table, typing it matched only `DISTANTLIGHT` in the
    // autocomplete, and Enter ran a light. The big button has its own id now.
    { big: { id: "util.measure", label: "Measure", cmd: "MEASUREGEOM", big: true,
             hint: "Click point to point along what you are measuring; the run adds up.",
             flyout: [
               { id: "util.measure.dist", label: "Distance", cmd: "DIST", key: "DI" },
               { id: "util.measure.radius", label: "Radius" },
               { id: "util.measure.angle", label: "Angle" },
               { id: "util.measure.area", label: "Area", cmd: "AREA", key: "AA" },
               { id: "util.measure.volume", label: "Volume" },
               { id: "util.measure.quick", label: "Quick" },
             ] } },
    { col: [
      { id: "util.quickselect", label: "Quick Select", cmd: "QSELECT" },
      { id: "util.calc", label: "Quick Calculator", cmd: "QUICKCALC", key: "Ctrl+8" },
      { id: "util.id", label: "ID Point", cmd: "ID" },
    ] },
  ],
  more: [
    { col: [
      { id: "util.selectsimilar", label: "Select Similar", cmd: "SELECTSIMILAR" },
      { id: "util.addselected", label: "Add Selected", cmd: "ADDSELECTED" },
      // ⚠ LOCAL has no AutoCAD counterpart — it is this app's own, and it is named for what it
      // does rather than for anything a drafter has typed before: the drawing moves into the
      // browser and every command after it is answered there.
      { id: "util.local", label: "Work Locally", cmd: "LOCAL" },
      // ⚠ RENDERER says which canvas backend is drawing and what the driver calls itself. No
      // AutoCAD counterpart either; it exists because nothing outside the app could find out,
      // and the desktop build measured three to seven times slower than the browser on the same
      // code with no way to tell whether it had quietly fallen back to Canvas2D.
      { id: "util.renderer", label: "Renderer", cmd: "RENDERER" },
      // ⚠⚠ The way BACK, and it belongs beside LOCAL because without it LOCAL is one-way: the
      // browser engine runs a fraction of the server's commands, so a drafter who takes a
      // drawing into the tab and then needs TRIM has to be able to hand it back. It SAVES first.
      { id: "util.server", label: "Back to the Server", cmd: "SERVER" },
      { id: "util.opendwg", label: "Open from this PC", cmd: "OPENDWG" },
      // ⚠ SAVEDXF is the other half of OPENDWG: the file is written in the tab that holds the
      // drawing, so a drawing that never reached the server can still be handed to a client.
      { id: "util.savedxf", label: "Save to this PC", cmd: "SAVEDXF" },
      // ⚠ the .dwg the drafter handed over, given back as itself — see `saveDwgToDisk`
      { id: "util.savedwg", label: "Save as DWG", cmd: "SAVEDWG" },
      // ⚠ and into the office system — phase 5's storage, through the import a drafter knows
      { id: "util.upload", label: "Send to the server", cmd: "UPLOAD" },
      { id: "util.purge", label: "Purge...", cmd: "PURGE", key: "PU" },
    ] },
  ],
};

// ⚠⚠ **THIS PANEL IS MOSTLY LIVE NOW — the paragraph that used to be here said the opposite.**
// It read "EVERY BUTTON ON THIS PANEL IS GREY, AND THAT IS THE POINT: AutoCAD has a clipboard;
// this studio does not", and it was true when it was written. Cut, Copy and Paste-to-Original
// were built on 2026-08-31 (`A.clipCut` / `A.clipCopy` / `A.clipPasteOrig`, with `T.clipCount`
// telling the shell whether there is anything to paste), and the banner stayed. Flagged in
// `DESIGN_ROADMAP.md` by the SketchUp lane, which correctly would not edit this file.
//
// ⚠ Worth saying why that matters more here than in most files: this shell's whole method is
// that a capitalised comment is load-bearing — it records a decision somebody had to make twice.
// One that has quietly become false teaches the next reader a rule the code does not follow, and
// it is the hardest kind to catch, because it still READS as authoritative.
//
// ⚠ What is still grey and why: `clip.paste` (Ctrl+V) and `clip.pastespec` need the SYSTEM
// clipboard, which is a permission prompt and a different problem from an in-app one;
// `clip.pasteblock` needs a block definition made from a selection, which is BLOCK, also unbuilt.
// Paste-to-Original is the one that works, because it pastes what THIS app copied, where it was.
const CLIPBOARD: AcadPanel = {
  id: "p.clip", label: "Clipboard",
  groups: [
    { big: { id: "clip.paste", label: "Paste", cmd: "PASTECLIP", key: "Ctrl+V", big: true,
             flyout: [
               { id: "clip.paste", label: "Paste", cmd: "PASTECLIP" },
               // ⚠ The key is declared HERE as well as in the Edit menu, because `BTN_BY_ID`
               // takes the first registration of an id and the ribbon is walked first — so a
               // key only the menu knew about was dropped from the index the shortcut binder
               // reads, and Ctrl+Shift+V printed in the menu and did nothing.
               { id: "clip.pasteblock", label: "Paste as Block", cmd: "PASTEBLOCK",
                 key: "Ctrl+Shift+V" },
               { id: "clip.pasteorig", label: "Paste to Original Coordinates", cmd: "PASTEORIG" },
               { id: "clip.pastespec", label: "Paste Special...", cmd: "PASTESPEC" },
             ] } },
    { col: [
      { id: "clip.cut", label: "Cut", cmd: "CUTCLIP", key: "Ctrl+X" },
      { id: "clip.copy", label: "Copy", cmd: "COPYCLIP", key: "Ctrl+C" },
      { id: "clip.copybase", label: "Copy with Base Point", cmd: "COPYBASE", key: "Ctrl+Shift+C" },
    ] },
  ],
};

const HOME_VIEW: AcadPanel = {
  id: "p.homeview", label: "View",
  groups: [
    { big: { id: "view.base", label: "Base", cmd: "VIEWBASE", big: true } },
    { col: [
      { id: "view.named", label: "Named Views", cmd: "VIEW", key: "V" },
      { id: "view.vports", label: "Viewport Configuration", cmd: "VPORTS" },
      { id: "view.zoom.extents", label: "Zoom Extents", cmd: "ZOOM", key: "Z",
        hint: "Frames the whole drawing." },
    ] },
  ],
};

// ── the other tabs ───────────────────────────────────────────────────────────────────────────

const INSERT_TAB: AcadPanel[] = [
  { id: "p.i.block", label: "Block", groups: [
    { big: { id: "block.insert", label: "Insert", cmd: "INSERT", key: "I", big: true } },
    { col: [
      { id: "block.edit", label: "Edit Attribute", cmd: "EATTEDIT" },
      { id: "block.attsync", label: "Synchronize", cmd: "ATTSYNC" },
      { id: "block.attmanage", label: "Manage Attributes", cmd: "BATTMAN" },
    ] },
  ] },
  { id: "p.i.blockdef", label: "Block Definition", groups: [
    { big: { id: "block.create", label: "Create Block", cmd: "BLOCK", key: "B", big: true } },
    { col: [
      { id: "block.attdef", label: "Define Attributes", cmd: "ATTDEF" },
      { id: "block.editor", label: "Block Editor", cmd: "BEDIT", key: "BE" },
      { id: "block.wblock", label: "Write Block", cmd: "WBLOCK", key: "W" },
    ] },
    // ⚠ AutoCAD puts these on a CONTEXTUAL ribbon tab that only exists inside the block editor.
    // There is no separate editing space here — the definition is edited on the drawing — so
    // they live beside BEDIT and are grey until something is open. Same information, one tab.
    { col: [
      { id: "block.savedef", label: "Save Block", cmd: "BSAVE" },
      { id: "block.discard", label: "Discard Edit", cmd: "BCLOSE" },
    ] },
  ] },
  { id: "p.i.ref", label: "Reference", launcher: { id: "insert.xref", label: "External References",
                                                   cmd: "EXTERNALREFERENCES" },
    groups: [
      { big: { id: "insert.attach", label: "Attach", cmd: "ATTACH", big: true } },
      // ⚠⚠ IMAGEATTACH is its own command, not a flavour of ATTACH: ours brings a DRAWING's
      // geometry in once, and this brings a PICTURE in and keeps the pixels. `IMAGE` is in
      // 21.4% of this office's drawings and pdcad drew every one as an empty rectangle.
      { big: { id: "insert.imageattach", label: "Attach Image", cmd: "IMAGEATTACH", big: true,
               hint: "Bring a photo or scan in and drag the rectangle it fills" } },
      // ⚠⚠ PDF ONLY, and MEASURED: across 18,241 office drawings `PDFUNDERLAY` is in 207 and
      // `DWFUNDERLAY` in NONE. AutoCAD's ribbon has DWFATTACH and DGNATTACH beside this one;
      // adding them here would be two more buttons for a format nobody in this office sends.
      { big: { id: "insert.pdfattach", label: "Attach PDF", cmd: "PDFATTACH", big: true,
               hint: "Place a PDF page as an underlay to trace over" } },
      { col: [
        { id: "insert.clip", label: "Clip", cmd: "CLIP" },
        { id: "insert.adjust", label: "Adjust", cmd: "IMAGEADJUST" },
        { id: "insert.uframe", label: "Frames vary", cmd: "FRAME" },
      ] },
      // ⚠ NOT in AutoCAD's ribbon — its CLIP command asks "New boundary / Delete" at the
      // prompt, and this command line cannot hold a multi-step prompt (the same reason
      // POLYGON's side count is asked for on the options strip). So the DELETE half gets its
      // own button
      // rather than being unreachable. It is the one addition to this panel and it is here
      // because the alternative is a clip nobody can undo.
      { col: [
        { id: "insert.clipdel", label: "Remove Clip", cmd: "CLIP" },
      ] },
      // ⚠ AutoCAD's underlay Fade IS our underlay opacity, backwards. The traced photo the
      // drawing was built over is an underlay in both products, and this is the only slider
      // either of them puts on it, so it is wired rather than rebuilt somewhere of our own.
      { wide: { id: "insert.underlay.fade", label: "Fade", control: "slider", width: 150,
                hint: "How far back the traced photo sits behind the drawing." } },
    ] },
  { id: "p.i.import", label: "Import", groups: [
    { big: { id: "insert.import", label: "Import", cmd: "IMPORT", big: true } },
    { col: [
      { id: "insert.pdfimport", label: "PDF Import", cmd: "PDFIMPORT" },
      { id: "insert.pdflayers", label: "PDF Layers" },
    ] },
  ] },
  { id: "p.i.data", label: "Linking & Extraction", groups: [
    // ⚠ AutoCAD's DATAEXTRACTION pulls a schedule out of the drawing's blocks and attributes.
    // That is what the quantity takeoff on the drawing's data sheet is, so the button goes
    // there rather than opening a wizard that would build the same table a second time.
    { big: { id: "insert.dataextract", label: "Extract Data", cmd: "DATAEXTRACTION", big: true,
             hint: "Opens the drawing's quantity takeoff." } },
    { col: [
      { id: "insert.datalink", label: "Data Link", cmd: "DATALINK" },
      { id: "insert.updatefields", label: "Update Fields", cmd: "UPDATEFIELD" },
      { id: "insert.field", label: "Field...", cmd: "FIELD" },
    ] },
  ] },
  { id: "p.i.content", label: "Content", groups: [
    { col: [
      { id: "pal.toolpalettes", label: "Tool Palettes", cmd: "TOOLPALETTES", key: "Ctrl+3" },
      { id: "pal.designcenter", label: "DesignCenter", cmd: "ADCENTER", key: "Ctrl+2" },
    ] },
  ] },
];

const ANNOTATE_TAB: AcadPanel[] = [
  { id: "p.a.text", label: "Text",
    launcher: { id: "anno.style.text", label: "Text Style", cmd: "STYLE" },
    groups: [
      { big: { id: "anno.text.single", label: "Text", cmd: "TEXT", key: "DT", big: true,
               flyout: [
                 { id: "anno.text.multi", label: "Multiline Text", cmd: "MTEXT" },
                 { id: "anno.text.single", label: "Single Line Text", cmd: "TEXT" },
               ] } },
      { col: [
        { id: "anno.text.find", label: "Find text", cmd: "FIND" },
        { id: "anno.text.spell", label: "Check Spelling", cmd: "SPELL" },
        { id: "anno.text.scale", label: "Scale", cmd: "SCALETEXT" },
      ] },
    ] },
  { id: "p.a.dims", label: "Dimensions",
    launcher: { id: "anno.style.dim", label: "Dimension Style", cmd: "DIMSTYLE" },
    groups: [
      { big: { id: "anno.dim.aligned", label: "Aligned", cmd: "DIMALIGNED", key: "DAL",
               big: true } },
      { col: [
        { id: "anno.dim.linear", label: "Linear", cmd: "DIMLINEAR", key: "DLI" },
        { id: "anno.dim.angular", label: "Angular", cmd: "DIMANGULAR", key: "DAN" },
        { id: "anno.dim.radius", label: "Radius", cmd: "DIMRADIUS", key: "DRA" },
      ] },
      { col: [
        { id: "anno.dim.diameter", label: "Diameter", cmd: "DIMDIAMETER", key: "DDI" },
        { id: "anno.dim.arc", label: "Arc Length", cmd: "DIMARC" },
        { id: "anno.dim.ordinate", label: "Ordinate", cmd: "DIMORDINATE" },
      ] },
      { col: [
        { id: "anno.dim.continue", label: "Continue", cmd: "DIMCONTINUE" },
        { id: "anno.dim.baseline", label: "Baseline", cmd: "DIMBASELINE" },
        { id: "anno.dim.quick", label: "Quick", cmd: "QDIM" },
      ] },
      { col: [
        { id: "anno.dim.break", label: "Break", cmd: "DIMBREAK" },
        { id: "anno.dim.space", label: "Adjust Space", cmd: "DIMSPACE" },
        { id: "anno.dim.jogline", label: "Jog Line", cmd: "DIMJOGLINE" },
      ] },
    ] },
  { id: "p.a.centre", label: "Centerlines", groups: [
    { col: [
      { id: "anno.centermark", label: "Center Mark", cmd: "CENTERMARK" },
      { id: "anno.centerline", label: "Centerline", cmd: "CENTERLINE" },
    ] },
  ] },
  { id: "p.a.leaders", label: "Leaders",
    launcher: { id: "anno.style.mleader", label: "Multileader Style", cmd: "MLEADERSTYLE" },
    groups: [
      { big: { id: "anno.leader", label: "Leader", cmd: "MLEADER", key: "MLE", big: true } },
      { col: [
        { id: "anno.leader.add", label: "Add Leader" },
        { id: "anno.leader.align", label: "Align", cmd: "MLEADERALIGN" },
        { id: "anno.leader.collect", label: "Collect", cmd: "MLEADERCOLLECT" },
      ] },
    ] },
  { id: "p.a.tables", label: "Tables",
    launcher: { id: "anno.style.table", label: "Table Style", cmd: "TABLESTYLE" },
    groups: [
      { big: { id: "anno.table", label: "Table", cmd: "TABLE", big: true } },
      { col: [
        { id: "anno.table.extract", label: "Extract Data", cmd: "DATAEXTRACTION" },
        { id: "anno.table.link", label: "Link Data", cmd: "DATALINK" },
      ] },
    ] },
  { id: "p.a.markup", label: "Markup", groups: [
    { col: [
      { id: "draw.revcloud", label: "Revision Cloud", cmd: "REVCLOUD" },
      { id: "draw.wipeout", label: "Wipeout", cmd: "WIPEOUT" },
    ] },
  ] },
  { id: "p.a.scaling", label: "Annotation Scaling", groups: [
    { col: [
      { id: "anno.addscale", label: "Add Current Scale", cmd: "OBJECTSCALE" },
      { id: "anno.syncscale", label: "Sync Scale Positions", cmd: "ANNORESET" },
      { id: "anno.visibility", label: "Annotation Visibility", cmd: "ANNOALLVISIBLE" },
    ] },
  ] },
];

const PARAMETRIC_TAB: AcadPanel[] = [
  { id: "p.p.geom", label: "Geometric", groups: [
    { big: { id: "par.autoconstrain", label: "Auto\nConstrain", cmd: "AUTOCONSTRAIN", big: true } },
    { col: [
      { id: "par.coincident", label: "Coincident", cmd: "GEOMCONSTRAINT" },
      { id: "par.collinear", label: "Collinear" },
      { id: "par.concentric", label: "Concentric" },
    ] },
    { col: [
      { id: "par.parallel", label: "Parallel" },
      { id: "par.perpendicular", label: "Perpendicular" },
      { id: "par.tangent", label: "Tangent" },
    ] },
    { col: [
      { id: "par.horizontal", label: "Horizontal" },
      { id: "par.vertical", label: "Vertical" },
      { id: "par.equal", label: "Equal" },
    ] },
    { col: [
      { id: "par.showall", label: "Show All", cmd: "CONSTRAINTBAR" },
      { id: "par.hideall", label: "Hide All" },
    ] },
  ] },
  { id: "p.p.dim", label: "Dimensional", groups: [
    { big: { id: "par.dimconstraint", label: "Linear", cmd: "DIMCONSTRAINT", big: true } },
    { col: [
      { id: "par.dim.aligned", label: "Aligned" },
      { id: "par.dim.angular", label: "Angular" },
      { id: "par.dim.radial", label: "Radial" },
    ] },
  ] },
  { id: "p.p.manage", label: "Manage", groups: [
    { col: [
      { id: "par.paramgr", label: "Parameters Manager", cmd: "PARAMETERS" },
      { id: "par.delcon", label: "Delete Constraints", cmd: "DELCONSTRAINT" },
    ] },
  ] },
];

/** AutoCAD's ten visual styles, in its own order. Shared by the View tab and the Visualize tab
 *  because they ARE the same control — AutoCAD shows it in both places, and two copies of the
 *  list would be two places for a new style to be forgotten. */
const VSTYLE_FLYOUT: AcadBtn[] = [
  { id: "vs.2dwire", label: "2D Wireframe" },
  { id: "vs.wire", label: "Wireframe" },
  { id: "vs.hidden", label: "Hidden" },
  { id: "vs.realistic", label: "Realistic" },
  { id: "vs.conceptual", label: "Conceptual" },
  { id: "vs.shaded", label: "Shaded" },
  { id: "vs.shadededges", label: "Shaded with Edges" },
  { id: "vs.gray", label: "Shades of Gray" },
  { id: "vs.sketchy", label: "Sketchy" },
  { id: "vs.xray", label: "X-Ray" },
];

const VIEW_TAB: AcadPanel[] = [
  // ⚠⚠ THE COORDINATES PANEL WAS MISSING, AND ITS ABSENCE WAS A LIE THE KEYBOARD CONTRADICTED.
  // AutoCAD's View tab has one, and the acad keymap here already binds `UCS` to the studio's
  // Axes tool — so a drafter could type UCS, watch the origin move, and find no button anywhere
  // in the ribbon that admitted the command existed. A shell whose keyboard can do more than
  // its ribbon shows is the same defect as a button that does nothing, wearing the other face.
  { id: "p.v.coords", label: "Coordinates", groups: [
    { big: { id: "ucs.set", label: "UCS", cmd: "UCS", big: true } },
    { col: [
      { id: "ucs.world", label: "World", cmd: "UCSWORLD" },
      { id: "ucs.previous", label: "Previous", cmd: "UCSPREV" },
      { id: "ucs.manager", label: "UCS Manager", cmd: "UCSMAN" },
    ] },
    { col: [
      { id: "view.ucsicon", label: "Show UCS Icon", cmd: "UCSICON" },
    ] },
  ] },
  // AutoCAD's View tab carries the visual styles too, not only the pull-down menu.
  { id: "p.v.vstyles", label: "Visual Styles", groups: [
    { big: { id: "view.visualstyles", label: "Visual\nStyles", cmd: "VISUALSTYLES", big: true,
             flyout: VSTYLE_FLYOUT } },
    { col: [
      { id: "view.3dviews", label: "3D Views", cmd: "VIEW",
        flyout: [
          { id: "view.3d.top", label: "Top" },
          { id: "view.3d.bottom", label: "Bottom" },
          { id: "view.3d.front", label: "Front" },
          { id: "view.3d.back", label: "Back" },
          { id: "view.3d.left", label: "Left" },
          { id: "view.3d.right", label: "Right" },
          { id: "view.3d.swiso", label: "SW Isometric" },
          { id: "view.3d.seiso", label: "SE Isometric" },
          { id: "view.3d.neiso", label: "NE Isometric" },
          { id: "view.3d.nwiso", label: "NW Isometric" },
        ] },
      { id: "view.orbit", label: "Orbit", cmd: "3DORBIT" },
      { id: "view.walk", label: "Walk", cmd: "3DWALK" },
    ] },
  ] },
  { id: "p.v.viewport", label: "Viewport Tools", groups: [
    { col: [
      { id: "view.ucsicon", label: "UCS Icon", cmd: "UCSICON",
        hint: "The X/Y marker in the corner of the drawing." },
      { id: "view.viewcube", label: "ViewCube", cmd: "NAVVCUBE" },
      { id: "view.navbar", label: "Navigation Bar", cmd: "NAVBAR" },
    ] },
  ] },
  { id: "p.v.navigate", label: "Navigate", groups: [
    { big: { id: "view.zoom.extents", label: "Zoom\nExtents", cmd: "ZOOM", key: "Z", big: true,
             flyout: [
               { id: "view.zoom.extents", label: "Zoom Extents", cmd: "ZOOM" },
               { id: "view.zoom.window", label: "Zoom Window" },
               { id: "view.zoom.prev", label: "Zoom Previous" },
               { id: "view.zoom.realtime", label: "Zoom Realtime" },
               { id: "view.zoom.all", label: "Zoom All" },
             ] } },
    { col: [
      { id: "view.pan", label: "Pan", cmd: "PAN", key: "P" },
      { id: "view.orbit", label: "Orbit", cmd: "3DORBIT" },
      { id: "view.regen", label: "Regenerate", cmd: "REGEN", key: "RE" },
    ] },
  ] },
  { id: "p.v.named", label: "Named Views", groups: [
    { big: { id: "view.named", label: "View\nManager", cmd: "VIEW", key: "V", big: true } },
  ] },
  { id: "p.v.vports", label: "Model Viewports", groups: [
    { big: { id: "view.vports", label: "Viewport\nConfiguration", cmd: "VPORTS", big: true } },
    { col: [
      { id: "view.vports.join", label: "Join" },
      { id: "view.vports.restore", label: "Restore" },
    ] },
  ] },
  { id: "p.v.palettes", label: "Palettes", groups: [
    { big: { id: "prop.palette", label: "Properties", cmd: "PROPERTIES", key: "Ctrl+1", big: true } },
    { col: [
      { id: "layer.props", label: "Layer Properties", cmd: "LAYER", key: "LA" },
      { id: "pal.toolpalettes", label: "Tool Palettes", cmd: "TOOLPALETTES", key: "Ctrl+3" },
      { id: "pal.blocks", label: "Blocks", cmd: "BLOCKSPALETTE" },
    ] },
    { col: [
      { id: "pal.designcenter", label: "DesignCenter", cmd: "ADCENTER", key: "Ctrl+2" },
      { id: "pal.sheetset", label: "Sheet Set Manager", cmd: "SHEETSET", key: "Ctrl+4" },
      { id: "pal.xref", label: "External References", cmd: "EXTERNALREFERENCES" },
    ] },
    { col: [
      { id: "pal.markup", label: "Markup Set Manager", cmd: "MARKUP", key: "Ctrl+7" },
      { id: "util.calc", label: "QuickCalc", cmd: "QUICKCALC", key: "Ctrl+8" },
      { id: "pal.history", label: "Drawing History", cmd: "DWGHISTORY" },
    ] },
  ] },
  { id: "p.v.interface", label: "Interface", groups: [
    { col: [
      { id: "ui.cleanscreen", label: "Clean Screen", cmd: "CLEANSCREENON", key: "Ctrl+0" },
      { id: "ui.filetabs", label: "File Tabs", cmd: "FILETAB" },
      { id: "ui.layouttabs", label: "Layout Tabs" },
    ] },
    { col: [
      { id: "ui.cmdline", label: "Command Line", cmd: "COMMANDLINE", key: "Ctrl+9" },
      { id: "ui.menubar", label: "Menu Bar", cmd: "MENUBAR" },
      { id: "ui.toolbars", label: "Toolbars", cmd: "TOOLBAR" },
    ] },
  ] },
];

const MANAGE_TAB: AcadPanel[] = [
  { id: "p.m.action", label: "Action Recorder", groups: [
    { big: { id: "man.record", label: "Record", cmd: "ACTRECORD", big: true } },
    { col: [
      { id: "man.play", label: "Play", cmd: "ACTSTOP" },
      { id: "man.usermsg", label: "Insert Message", cmd: "ACTUSERMESSAGE" },
      { id: "man.pref", label: "Preferences", cmd: "ACTUSERINPUT" },
    ] },
  ] },
  { id: "p.m.custom", label: "Customization", groups: [
    { big: { id: "man.cui", label: "User\nInterface", cmd: "CUI", big: true } },
    { col: [
      { id: "man.editaliases", label: "Edit Aliases", cmd: "AIALIASEDIT" },
      { id: "man.tp", label: "Tool Palettes", cmd: "CUSTOMIZE" },
    ] },
  ] },
  { id: "p.m.apps", label: "Applications", groups: [
    { big: { id: "man.loadapp", label: "Load\nApplication", cmd: "APPLOAD", big: true } },
    { col: [
      { id: "man.runscript", label: "Run Script", cmd: "SCRIPT" },
      { id: "man.vlisp", label: "Visual LISP Editor", cmd: "VLISP" },
      { id: "man.vba", label: "VBA Manager", cmd: "VBAMAN" },
    ] },
  ] },
  { id: "p.m.standards", label: "CAD Standards", groups: [
    { col: [
      { id: "man.configstd", label: "Configure", cmd: "STANDARDS" },
      { id: "man.checkstd", label: "Check", cmd: "CHECKSTANDARDS" },
      { id: "man.layertrans", label: "Layer Translator", cmd: "LAYTRANS" },
    ] },
  ] },
];

const OUTPUT_TAB: AcadPanel[] = [
  { id: "p.o.plot", label: "Plot",
    launcher: { id: "out.pagesetup", label: "Page Setup Manager", cmd: "PAGESETUP" },
    groups: [
      { big: { id: "out.plot", label: "Plot", cmd: "PLOT", key: "Ctrl+P", big: true,
               hint: "Prints the composed sheet set, or an A3 of the drawing if there is none." } },
      { col: [
        { id: "out.preview", label: "Preview", cmd: "PREVIEW" },
        { id: "out.batchplot", label: "Batch Plot", cmd: "PUBLISH" },
        { id: "out.viewdetails", label: "View Details", cmd: "PLOTSTAMP" },
      ] },
      { col: [
        { id: "out.plottermanager", label: "Plotter Manager", cmd: "PLOTTERMANAGER" },
        { id: "out.plotstyle", label: "Plot Style Manager", cmd: "STYLESMANAGER" },
        { id: "out.pagesetup", label: "Page Setup Manager", cmd: "PAGESETUP" },
      ] },
    ] },
  { id: "p.o.export", label: "Export to DWF/PDF", groups: [
    { big: { id: "out.exportpdf", label: "Export\nPDF", cmd: "EXPORTPDF", big: true,
             flyout: [
               { id: "out.exportpdf", label: "PDF", cmd: "EXPORTPDF" },
               { id: "file.export.dxf", label: "DXF", cmd: "DXFOUT" },
               { id: "out.exportdwf", label: "DWF", cmd: "EXPORTDWF" },
               { id: "out.exportdgn", label: "DGN", cmd: "DGNEXPORT" },
               { id: "out.exportfbx", label: "FBX", cmd: "FBXEXPORT" },
               { id: "out.exportstl", label: "STL", cmd: "STLOUT" },
             ] } },
    { col: [
      { id: "out.exportlayout", label: "Export Layout", cmd: "EXPORTLAYOUT" },
      { id: "out.exportsetup", label: "Export Setup", cmd: "PDFSHXTEXT" },
    ] },
  ] },
];

const ADDINS_TAB: AcadPanel[] = [
  { id: "p.x.content", label: "Content", groups: [
    { big: { id: "addin.appmanager", label: "App\nManager", cmd: "APPAUTOLOADER", big: true } },
    { col: [
      { id: "addin.appstore", label: "Autodesk App Store" },
      { id: "addin.plugins", label: "Plug-ins" },
    ] },
  ] },
];

const COLLAB_TAB: AcadPanel[] = [
  { id: "p.c.share", label: "Share", groups: [
    { big: { id: "col.share", label: "Share", cmd: "SHARE", big: true } },
    { col: [
      { id: "col.trace", label: "Trace", cmd: "TRACE" },
      { id: "col.markupimport", label: "Markup Import", cmd: "MARKUPASSIST" },
    ] },
  ] },
  { id: "p.c.compare", label: "Compare", groups: [
    { big: { id: "col.compare", label: "DWG\nCompare", cmd: "COMPARE", big: true } },
    { col: [
      { id: "col.xrefcompare", label: "Xref Compare", cmd: "XCOMPARE" },
    ] },
  ] },
  { id: "p.c.history", label: "Drawing History", groups: [
    // ⚠ AutoCAD 2021 added DWGHISTORY — the drawing's own saved versions, side by side. That is
    // exactly the studio's History panel, so it is wired to it rather than rebuilt. The panel
    // says its own scope up front: geometry only, not sheets or schedules.
    { big: { id: "pal.history", label: "Drawing\nHistory", cmd: "DWGHISTORY", big: true,
             hint: "The drawing's saved versions — geometry only." } },
  ] },
];

const EXPRESS_TAB: AcadPanel[] = [
  { id: "p.e.blocks", label: "Blocks", groups: [
    { col: [
      { id: "exp.copym", label: "Multiple Copy", cmd: "COPYM" },
      { id: "exp.ncopy", label: "Copy Nested Objects", cmd: "NCOPY" },
      { id: "exp.explode", label: "Extended Explode", cmd: "XPLODE" },
    ] },
  ] },
  { id: "p.e.text", label: "Text", groups: [
    { col: [
      { id: "exp.txtexp", label: "Explode Text", cmd: "TXTEXP" },
      { id: "exp.arctext", label: "Arc-Aligned Text", cmd: "ARCTEXT" },
      { id: "exp.txt2mtxt", label: "Convert to Mtext", cmd: "TXT2MTXT" },
    ] },
  ] },
  { id: "p.e.modify", label: "Modify", groups: [
    { col: [
      { id: "exp.overkill", label: "Delete Duplicate Objects", cmd: "OVERKILL" },
      { id: "exp.breakline", label: "Break-Line Symbol", cmd: "BREAKLINE" },
      { id: "exp.flatten", label: "Flatten Objects", cmd: "FLATTEN" },
    ] },
  ] },
  { id: "p.e.tools", label: "Tools", groups: [
    { col: [
      { id: "exp.saveall", label: "Save All Drawings", cmd: "SAVEALL" },
      // ⚠⚠ **A TRANSCRIPTION ERROR, FOUND BY WIRING IT.** This read `label: "Quick Select",
      // cmd: "QQUIT"` — two different Express Tools commands welded together. QQUIT is *Quick
      // QUIT*: it closes every open drawing. Quick Select is QSELECT and already exists at
      // `util.quickselect`. Wiring the button to Quick Select would have made typing QQUIT open
      // a selection dialog; wiring it to what the cmd says would have been worse.
      { id: "exp.qqui", label: "Quick Quit", cmd: "QQUIT" },
    ] },
  ] },
];

// ⚠⚠⚠ **THE PDVN TAB WAS REMOVED HERE, AND THE PARAGRAPH THAT USED TO ARGUE FOR IT WAS
// TRUE OF A DIFFERENT PRODUCT.** It said the tab holds "a wall tool, a door tool, a staircase
// solver and a build-up tool" that AutoCAD does not have, and that hiding it "would hide the
// wall, door, window and stair tools that are the whole reason the drawing exists". That is the
// DESIGN app talking. It came across with the chrome and nobody re-read it against pdcad.
//
// ⚠ Measured before deleting, over the tab's 38 controls: **17 have no handler in this app at
// all** — `pdvn.wall`, `pdvn.door`, `pdvn.window`, `pdvn.stair`, `pdvn.buildup`, `pdvn.divider`,
// `pdvn.section`, `pdvn.detail`, `pdvn.setscale`, `pdvn.arrow`, the three booleans, and the four
// deliverables (IFC, Collada, OBJ, section slice). Seven more are grey. The only LIVE ones were
// `block.insert`, which is AutoCAD's own INSERT and already on the Home tab, and six palettes
// that open a box reading "Not built yet." Nothing on it was uniquely this product's.
//
// ⇒ pdcad IS the AutoCAD replacement, so there is no host application for a vendor tab to sit
// beside — this app has no wall, no door, no staircase and no 3D. A vendor tab advertising a
// building model to a drafter who opened a DWG is the same lie as a live button that does
// nothing, spread across a whole tab.
// ⚠ `tabsFor` filters through `TABS`, so removing the entry drops it from all three workspaces
// at once rather than leaving one of them rendering `undefined` — see `WS_TABS`.

// ── the tabs the 3D workspaces add ───────────────────────────────────────────────────────────
//
// ⚠⚠ **THESE ARE AUTOCAD'S REAL PANELS, NOT A SKETCH OF THEM.** The temptation with a workspace
// nobody has built the commands for is to invent three thin tabs that look plausible. That would
// make the shell a picture of AutoCAD instead of a map of it — and the map is the point: a
// drafter reads a grey button and learns "this product does not do that yet", which is true and
// useful, where an absent button teaches nothing at all. Every command below is one AutoCAD
// really has, in the panel it really lives in.
//
// ⚠⚠ **THE PARAGRAPH THAT USED TO SIT HERE LISTED WHAT IS "LIVE" ON THESE TABS, AND NONE OF
// IT IS LIVE IN THIS APP.** It said UNION, SUBTRACT and INTERSECT "are the studio's boolean
// solid ops (they sit on the PDVN tab too)", SECTIONPLANE "is our section tool", and "the whole
// Visualize tab is the 3D view's cameras and visual styles". That is the DESIGN app. pdcad has
// no 3D view, no solid modeller and no section tool — `package.json` has no three.js and the
// canvas is a 2D context — so every command on all three of these tabs is grey, which is the
// honest state and the reason the tabs are kept: a map of what AutoCAD has and this does not.
// ⚠ The ids were the design app's (`pdvn.union`, `pdvn.section`); they are AutoCAD's own now,
// so the ribbon button, the pull-down item and the typed command are still one id.

const SOLID_TAB: AcadPanel[] = [
  { id: "p.s.prim", label: "Primitive", groups: [
    { big: { id: "sol.box", label: "Box", cmd: "BOX", big: true, flyout: [
      { id: "sol.box", label: "Box", cmd: "BOX" },
      { id: "sol.cylinder", label: "Cylinder", cmd: "CYLINDER" },
      { id: "sol.cone", label: "Cone", cmd: "CONE" },
      { id: "sol.sphere", label: "Sphere", cmd: "SPHERE" },
      { id: "sol.pyramid", label: "Pyramid", cmd: "PYRAMID" },
      { id: "sol.wedge", label: "Wedge", cmd: "WEDGE" },
      { id: "sol.torus", label: "Torus", cmd: "TORUS" },
    ] } },
    { col: [
      { id: "sol.polysolid", label: "Polysolid", cmd: "POLYSOLID" },
      { id: "sol.extrude", label: "Extrude", cmd: "EXTRUDE", key: "EXT" },
      { id: "sol.presspull", label: "Presspull", cmd: "PRESSPULL" },
    ] },
    { col: [
      { id: "sol.revolve", label: "Revolve", cmd: "REVOLVE", key: "REV" },
      { id: "sol.sweep", label: "Sweep", cmd: "SWEEP" },
      { id: "sol.loft", label: "Loft", cmd: "LOFT" },
    ] },
  ] },
  { id: "p.s.bool", label: "Boolean", groups: [
    { col: [
      { id: "mod.union", label: "Union", cmd: "UNION", key: "UNI" },
      { id: "mod.subtract", label: "Subtract", cmd: "SUBTRACT", key: "SU" },
      { id: "mod.intersect", label: "Intersect", cmd: "INTERSECT", key: "IN" },
    ] },
    { col: [
      { id: "sol.interfere", label: "Interfere", cmd: "INTERFERE" },
    ] },
  ] },
  { id: "p.s.edit", label: "Solid Editing", groups: [
    { col: [
      { id: "sol.slice", label: "Slice", cmd: "SLICE", key: "SL" },
      { id: "sol.thicken", label: "Thicken", cmd: "THICKEN" },
      { id: "sol.shell", label: "Shell", cmd: "SOLIDEDIT" },
    ] },
    { col: [
      { id: "sol.imprint", label: "Imprint", cmd: "IMPRINT" },
      { id: "sol.extractedges", label: "Extract Edges", cmd: "XEDGES" },
      { id: "sol.offsetedge", label: "Offset Edge", cmd: "OFFSETEDGE" },
    ] },
  ] },
  { id: "p.s.section", label: "Section", groups: [
    { big: { id: "sol.sectionplane", label: "Section\nPlane", cmd: "SECTIONPLANE",
             big: true } },
    { col: [
      { id: "sol.sectionblock", label: "Generate Section", cmd: "SECTIONPLANETOBLOCK" },
      { id: "sol.livesection", label: "Live Section", cmd: "LIVESECTION" },
    ] },
  ] },
];

const SURFACE_TAB: AcadPanel[] = [
  { id: "p.u.create", label: "Create", groups: [
    { big: { id: "surf.network", label: "Network", cmd: "SURFNETWORK", big: true } },
    { col: [
      { id: "surf.extrude", label: "Extrude", cmd: "EXTRUDE" },
      { id: "surf.loft", label: "Loft", cmd: "LOFT" },
      { id: "surf.revolve", label: "Revolve", cmd: "REVOLVE" },
    ] },
    { col: [
      { id: "surf.sweep", label: "Sweep", cmd: "SWEEP" },
      { id: "surf.planar", label: "Planar", cmd: "PLANESURF" },
      { id: "surf.blend", label: "Blend", cmd: "SURFBLEND" },
    ] },
    { col: [
      { id: "surf.patch", label: "Patch", cmd: "SURFPATCH" },
      { id: "surf.offset", label: "Offset", cmd: "SURFOFFSET" },
      { id: "surf.fillet", label: "Fillet", cmd: "SURFFILLET" },
    ] },
  ] },
  { id: "p.u.edit", label: "Edit", groups: [
    { col: [
      { id: "surf.trim", label: "Trim", cmd: "SURFTRIM" },
      { id: "surf.untrim", label: "Untrim", cmd: "SURFUNTRIM" },
      { id: "surf.extend", label: "Extend", cmd: "SURFEXTEND" },
    ] },
    { col: [
      { id: "surf.sculpt", label: "Sculpt", cmd: "SURFSCULPT" },
      { id: "surf.project", label: "Project", cmd: "PROJECTGEOMETRY" },
    ] },
  ] },
  { id: "p.u.analysis", label: "Analysis", groups: [
    { col: [
      { id: "surf.zebra", label: "Zebra", cmd: "ANALYSISZEBRA" },
      { id: "surf.curvature", label: "Curvature", cmd: "ANALYSISCURVATURE" },
      { id: "surf.draft", label: "Draft", cmd: "ANALYSISDRAFT" },
    ] },
  ] },
];

const MESH_TAB: AcadPanel[] = [
  { id: "p.m.prim", label: "Primitives", groups: [
    { big: { id: "mesh.box", label: "Mesh\nBox", cmd: "MESH", big: true, flyout: [
      { id: "mesh.box", label: "Mesh Box", cmd: "MESH" },
      { id: "mesh.cone", label: "Mesh Cone", cmd: "MESH" },
      { id: "mesh.cylinder", label: "Mesh Cylinder", cmd: "MESH" },
      { id: "mesh.pyramid", label: "Mesh Pyramid", cmd: "MESH" },
      { id: "mesh.sphere", label: "Mesh Sphere", cmd: "MESH" },
      { id: "mesh.wedge", label: "Mesh Wedge", cmd: "MESH" },
      { id: "mesh.torus", label: "Mesh Torus", cmd: "MESH" },
    ] } },
    { col: [
      { id: "mesh.revsurf", label: "Revolved Surface", cmd: "REVSURF" },
      { id: "mesh.edgesurf", label: "Edge Surface", cmd: "EDGESURF" },
      { id: "mesh.rulesurf", label: "Ruled Surface", cmd: "RULESURF" },
    ] },
  ] },
  { id: "p.m.mesh", label: "Mesh", groups: [
    { col: [
      { id: "mesh.smoothmore", label: "Smooth More", cmd: "MESHSMOOTHMORE" },
      { id: "mesh.smoothless", label: "Smooth Less", cmd: "MESHSMOOTHLESS" },
      { id: "mesh.refine", label: "Refine Mesh", cmd: "MESHREFINE" },
    ] },
    { col: [
      { id: "mesh.crease", label: "Add Crease", cmd: "MESHCREASE" },
      { id: "mesh.uncrease", label: "Remove Crease", cmd: "MESHUNCREASE" },
      { id: "mesh.split", label: "Split Face", cmd: "MESHSPLIT" },
    ] },
  ] },
  { id: "p.m.convert", label: "Convert Mesh", groups: [
    { col: [
      { id: "mesh.tosolid", label: "Convert to Solid", cmd: "CONVTOSOLID" },
      { id: "mesh.tosurface", label: "Convert to Surface", cmd: "CONVTOSURFACE" },
      { id: "mesh.smoothobj", label: "Smooth Object", cmd: "MESHSMOOTH" },
    ] },
  ] },
];

const VISUALIZE_TAB: AcadPanel[] = [
  { id: "p.b.styles", label: "Visual Styles",
    launcher: { id: "view.visualstyles", label: "Visual Styles Manager", cmd: "VISUALSTYLES" },
    groups: [
      { big: { id: "vs.realistic", label: "Visual\nStyles", cmd: "VSCURRENT", big: true,
               flyout: VSTYLE_FLYOUT } },
      { col: [
        { id: "vs.wire", label: "2D Wireframe", cmd: "VSCURRENT" },
        { id: "vs.hidden", label: "Hidden", cmd: "VSCURRENT" },
        { id: "vs.gray", label: "Shades of Gray", cmd: "VSCURRENT" },
      ] },
    ] },
  { id: "p.b.camera", label: "Camera", groups: [
    { big: { id: "view.orbit", label: "Orbit", cmd: "3DORBIT", big: true } },
    { col: [
      { id: "view.3d.top", label: "Top", cmd: "-VIEW" },
      { id: "view.3d.front", label: "Front", cmd: "-VIEW" },
      { id: "view.3d.left", label: "Left", cmd: "-VIEW" },
    ] },
    { col: [
      { id: "view.named", label: "View Manager", cmd: "VIEW", key: "V" },
      // ⚠ Reachable by NAME too — `TESTDRIVE` at the command line — because the person who needs
      // it most may be on a phone call describing where the button is.
      { id: "tools.testdrive", label: "Test Drive", cmd: "TESTDRIVE" },
      { id: "view.camera", label: "Create Camera", cmd: "CAMERA" },
      { id: "view.walk", label: "Walk", cmd: "3DWALK" },
    ] },
  ] },
  { id: "p.b.lights", label: "Lights", groups: [
    { col: [
      { id: "vis.pointlight", label: "Point Light", cmd: "POINTLIGHT" },
      { id: "vis.spotlight", label: "Spot Light", cmd: "SPOTLIGHT" },
      { id: "vis.distant", label: "Distant Light", cmd: "DISTANTLIGHT" },
    ] },
    { col: [
      { id: "vis.sun", label: "Sun Status", cmd: "SUNPROPERTIES" },
      { id: "vis.geoloc", label: "Set Location", cmd: "GEOGRAPHICLOCATION" },
    ] },
  ] },
  { id: "p.b.materials", label: "Materials", groups: [
    { col: [
      { id: "vis.matbrowser", label: "Materials Browser", cmd: "MATBROWSEROPEN" },
      { id: "vis.mateditor", label: "Materials Editor", cmd: "MATEDITOROPEN" },
      { id: "vis.matattach", label: "Attach by Layer", cmd: "MATERIALATTACH" },
    ] },
  ] },
  { id: "p.b.render", label: "Render",
    launcher: { id: "vis.renderpresets", label: "Render Presets", cmd: "RENDERPRESETS" },
    groups: [
      { big: { id: "vis.render", label: "Render\nto Size", cmd: "RENDER", big: true } },
      { col: [
        { id: "vis.rendercrop", label: "Render Region", cmd: "RENDERCROP" },
        { id: "vis.renderwin", label: "Render Window", cmd: "RENDERWINDOW" },
        { id: "vis.renderenv", label: "Environment", cmd: "RENDERENVIRONMENT" },
      ] },
    ] },
];

export const TABS: AcadTab[] = [
  { id: "home", label: "Home",
    panels: [HOME, MODIFY, ANNOTATION, LAYERS, BLOCK, PROPERTIES, GROUPS_PANEL, UTILITIES,
             CLIPBOARD, HOME_VIEW] },
  { id: "insert", label: "Insert", panels: INSERT_TAB },
  { id: "annotate", label: "Annotate", panels: ANNOTATE_TAB },
  { id: "parametric", label: "Parametric", panels: PARAMETRIC_TAB },
  { id: "view", label: "View", panels: VIEW_TAB },
  { id: "manage", label: "Manage", panels: MANAGE_TAB },
  { id: "output", label: "Output", panels: OUTPUT_TAB },
  { id: "addins", label: "Add-ins", panels: ADDINS_TAB },
  { id: "collaborate", label: "Collaborate", panels: COLLAB_TAB },
  { id: "express", label: "Express Tools", panels: EXPRESS_TAB },
  // ⚠ The three 3D tabs live in `TABS` like every other one — a workspace SELECTS tabs, it does
  // not own them. AutoCAD works the same way: Solid is one tab that two workspaces show.
  { id: "solid", label: "Solid", panels: SOLID_TAB },
  { id: "surface", label: "Surface", panels: SURFACE_TAB },
  { id: "mesh", label: "Mesh", panels: MESH_TAB },
  { id: "visualize", label: "Visualize", panels: VISUALIZE_TAB },
];

// ── the workspaces ───────────────────────────────────────────────────────────────────────────

/** Which ribbon tabs each workspace shows, in AutoCAD's own order.
 *
 * ⚠⚠ **A WORKSPACE IS A TAB SET, AND THAT IS GENUINELY ALL IT IS HERE.** In AutoCAD a workspace
 * also remembers palette positions, toolbars and the ribbon state; the honest subset for this
 * shell is the one thing a drafter actually notices when they switch, which is that the ribbon
 * grows a Solid tab and loses Parametric. Claiming more by saving palette layout per workspace
 * would be inventing a feature to fill out a menu.
 *
 * ⚠ There is no vendor tab in this list any more — see the note above the 3D tabs. Every tab
 * named here is one AutoCAD itself ships.
 */
export const WS_TABS: Record<string, string[]> = {
  "ws.drafting": ["home", "insert", "annotate", "parametric", "view", "manage", "output",
                  "addins", "collaborate", "express"],
  // ⚠ 3D Basics really is this short in AutoCAD — it is the beginner workspace, and its whole
  // point is that it does NOT show Solid, Surface and Mesh.
  "ws.3dbasics": ["home", "visualize", "insert", "view", "manage", "output"],
  "ws.3dmodel": ["home", "solid", "surface", "mesh", "visualize", "parametric", "insert",
                 "annotate", "view", "manage", "output", "addins", "collaborate", "express"],
};

/** The tabs for a workspace, falling back to Drafting & Annotation.
 *
 * ⚠ Filtered through `TABS` rather than built from it, so a tab id that gets renamed or removed
 * drops out of every workspace at once instead of leaving one of them rendering `undefined`. */
export function tabsFor(ws: string): AcadTab[] {
  const want = WS_TABS[ws] || WS_TABS["ws.drafting"];
  return want.map((id) => TABS.find((t) => t.id === id)).filter(Boolean) as AcadTab[];
}

// ── the Quick Access Toolbar ─────────────────────────────────────────────────────────────────

/** The QAT, left to right, as a fresh AutoCAD ships it. `null` is a separator. */
export const QAT: (string | null)[] = [
  "file.new", "file.open", "file.save", "file.saveas", null,
  "cloud.open", "cloud.save", null,
  "out.plot", null,
  "edit.undo", "edit.redo",
];

/** Which command each of the QAT menu's seven ticks puts on the toolbar.
 *
 * ⚠⚠ **ONE MAP, BECAUSE THE TICK AND THE BUTTON ARE THE SAME FACT.** The menu item is
 * `qat.save` and the button it shows runs `file.save`; written as two lists they can disagree,
 * and a tick that lights nothing is the exact failure this shell exists to avoid. `QAT` above
 * is the ORDER — this is the mapping — and the renderer filters one through the other.
 */
export const QAT_OF: Record<string, string> = {
  "qat.new": "file.new", "qat.open": "file.open", "qat.save": "file.save",
  "qat.saveas": "file.saveas", "qat.plot": "out.plot",
  "qat.undo": "edit.undo", "qat.redo": "edit.redo",
};
/** …and back, so the renderer can ask "is this button ticked on?" without a second table. */
export const QAT_TICK: Record<string, string> =
  Object.fromEntries(Object.entries(QAT_OF).map(([k, v]) => [v, k]));

/** The QAT's ▾ menu — every item is a tick, and the last two are commands. */
export const QAT_MENU: AcadMenuItem[] = [
  { id: "qat.new", label: "New", toggle: true },
  { id: "qat.open", label: "Open", toggle: true },
  { id: "qat.save", label: "Save", toggle: true },
  { id: "qat.saveas", label: "Save As", toggle: true },
  { id: "qat.plot", label: "Plot", toggle: true },
  { id: "qat.undo", label: "Undo", toggle: true },
  { id: "qat.redo", label: "Redo", toggle: true },
  S,
  { id: "qat.more", label: "More Commands..." },
  { id: "ui.menubar", label: "Show Menu Bar", toggle: true },
  { id: "qat.below", label: "Show Below the Ribbon" },
];

/** The workspace switcher, which lives on the QAT and again on the status bar. */
export const WORKSPACES: AcadMenuItem[] = [
  { id: "ws.drafting", label: "Drafting & Annotation", radio: true },
  { id: "ws.3dbasics", label: "3D Basics", radio: true },
  { id: "ws.3dmodel", label: "3D Modeling", radio: true },
  S,
  { id: "ws.saveas", label: "Save Current As..." },
  { id: "ws.settings", label: "Workspace Settings..." },
  { id: "man.cui", label: "Customize..." },
];

// ── the Application menu (the big A) ─────────────────────────────────────────────────────────

export const APP_MENU: AcadMenuItem[] = [
  { id: "file.new", label: "New", sub: [
    { id: "file.new", label: "Drawing" },
    { id: "file.new.sheetset", label: "Sheet Set" },
  ] },
  { id: "file.open", label: "Open", sub: [
    { id: "file.open", label: "Drawing" },
    { id: "file.open.sheetset", label: "Sheet Set" },
    { id: "file.open.dgn", label: "DGN" },
  ] },
  { id: "file.save", label: "Save" },
  { id: "file.saveas", label: "Save As", sub: [
    { id: "file.saveas", label: "AutoCAD Drawing" },
    { id: "file.saveas.template", label: "AutoCAD Drawing Template" },
    { id: "file.export.dxf", label: "AutoCAD DXF" },
    { id: "file.saveas.other", label: "Other Formats" },
  ] },
  { id: "file.import", label: "Import", sub: [
    { id: "insert.import", label: "Files of all supported formats" },
    { id: "insert.pdfimport", label: "PDF" },
  ] },
  { id: "file.export", label: "Export", sub: [
    { id: "out.exportpdf", label: "PDF" },
    { id: "file.export.dxf", label: "DXF" },
    { id: "out.exportdwf", label: "DWF" },
    { id: "out.exportdgn", label: "DGN" },
    { id: "out.exportfbx", label: "FBX" },
    { id: "out.exportstl", label: "STL" },
  ] },
  { id: "file.publish", label: "Publish", sub: [
    { id: "out.batchplot", label: "Batch Plot" },
    { id: "file.etransmit", label: "eTransmit" },
    { id: "file.archive", label: "Archive" },
  ] },
  { id: "file.printgroup", label: "Print", sub: [
    { id: "out.plot", label: "Plot" },
    { id: "out.preview", label: "Preview" },
    { id: "out.pagesetup", label: "Page Setup" },
    { id: "out.plottermanager", label: "Manage Plotters" },
  ] },
  { id: "file.drawingutils", label: "Drawing Utilities", sub: [
    { id: "util.purge", label: "Purge" },
    { id: "file.audit", label: "Audit" },
    { id: "file.recover", label: "Recover" },
    { id: "file.units", label: "Units" },
  ] },
  { id: "file.close", label: "Close", sub: [
    { id: "file.close", label: "Current Drawing" },
    { id: "file.closeall", label: "All Drawings" },
  ] },
];

// ── the classic pull-down menu bar (MENUBAR = 1) ─────────────────────────────────────────────

/** ⚠ Off by default, because AutoCAD's is: MENUBAR has shipped at 0 since 2009 and the ribbon
 *  replaced it. It is here because a drafter of twenty years still reaches for Draw ▸ Line, and
 *  because turning it on is one click in the QAT's own ▾ menu, exactly where AutoCAD keeps it. */
export const MENUS: AcadMenu[] = [
  { label: "File", items: [
    { id: "file.new", label: "New...", key: "Ctrl+N" },
    { id: "file.new.sheetset", label: "New Sheet Set..." },
    { id: "file.open", label: "Open...", key: "Ctrl+O" },
    { id: "file.open.sheetset", label: "Open Sheet Set..." },
    { id: "file.close", label: "Close" },
    S,
    { id: "file.save", label: "Save", key: "Ctrl+S" },
    { id: "file.saveas", label: "Save As...", key: "Ctrl+Shift+S" },
    { id: "insert.dataextract", label: "eTransmit..." },
    S,
    { id: "file.export", label: "Export", sub: [
      { id: "out.exportpdf", label: "PDF..." },
      { id: "file.export.dxf", label: "DXF..." },
      { id: "out.exportdwf", label: "DWF..." },
    ] },
    { id: "insert.import", label: "Import..." },
    S,
    { id: "out.pagesetup", label: "Page Setup Manager..." },
    { id: "out.preview", label: "Plot Preview" },
    { id: "out.plot", label: "Plot...", key: "Ctrl+P" },
    { id: "out.batchplot", label: "Publish..." },
    S,
    { id: "file.props", label: "Drawing Properties..." },
    { id: "file.exit", label: "Exit", key: "Ctrl+Q" },
  ] },
  { label: "Edit", items: [
    { id: "edit.undo", label: "Undo", key: "Ctrl+Z" },
    { id: "edit.redo", label: "Redo", key: "Ctrl+Y" },
    S,
    { id: "clip.cut", label: "Cut", key: "Ctrl+X" },
    { id: "clip.copy", label: "Copy", key: "Ctrl+C" },
    { id: "clip.copybase", label: "Copy with Base Point", key: "Ctrl+Shift+C" },
    { id: "clip.paste", label: "Paste", key: "Ctrl+V" },
    { id: "clip.pasteblock", label: "Paste as Block", key: "Ctrl+Shift+V" },
    { id: "clip.pasteorig", label: "Paste to Original Coordinates" },
    { id: "clip.pastespec", label: "Paste Special..." },
    S,
    { id: "mod.erase", label: "Erase", key: "Del" },
    { id: "edit.selectall", label: "Select All", key: "Ctrl+A" },
    S,
    { id: "anno.text.find", label: "Find..." },
  ] },
  { label: "View", items: [
    { id: "view.regen", label: "Regen" },
    { id: "view.regenall", label: "Regen All" },
    S,
    { id: "view.zoomgroup", label: "Zoom", sub: [
      { id: "view.zoom.realtime", label: "Realtime" },
      { id: "view.zoom.prev", label: "Previous" },
      { id: "view.zoom.window", label: "Window" },
      { id: "view.zoom.all", label: "All" },
      { id: "view.zoom.extents", label: "Extents" },
    ] },
    { id: "view.pan", label: "Pan", sub: [
      { id: "view.pan", label: "Realtime" },
      { id: "view.pan.point", label: "Point" },
    ] },
    { id: "view.orbit", label: "Orbit", sub: [
      { id: "view.orbit", label: "Constrained Orbit" },
      { id: "view.orbit.free", label: "Free Orbit" },
      { id: "view.orbit.cont", label: "Continuous Orbit" },
    ] },
    S,
    { id: "view.named", label: "Named Views..." },
    { id: "view.3dviews", label: "3D Views", sub: [
      { id: "view.3d.top", label: "Top", radio: true },
      { id: "view.3d.bottom", label: "Bottom", radio: true },
      { id: "view.3d.left", label: "Left", radio: true },
      { id: "view.3d.right", label: "Right", radio: true },
      { id: "view.3d.front", label: "Front", radio: true },
      { id: "view.3d.back", label: "Back", radio: true },
      S,
      { id: "view.3d.swiso", label: "SW Isometric", radio: true },
      { id: "view.3d.seiso", label: "SE Isometric", radio: true },
      { id: "view.3d.neiso", label: "NE Isometric", radio: true },
      { id: "view.3d.nwiso", label: "NW Isometric", radio: true },
    ] },
    // ⚠ AutoCAD ships TEN visual styles and this listed six. The missing four included X-Ray
    // and Shades of Gray, which are the two our 3D view happens to have exactly — so the short
    // list was both unfaithful AND hiding working features.
    { id: "view.visualstyles", label: "Visual Styles", sub: [
      { id: "vs.2dwire", label: "2D Wireframe", radio: true },
      { id: "vs.wire", label: "Wireframe", radio: true },
      { id: "vs.hidden", label: "Hidden", radio: true },
      { id: "vs.realistic", label: "Realistic", radio: true },
      { id: "vs.conceptual", label: "Conceptual", radio: true },
      { id: "vs.shaded", label: "Shaded", radio: true },
      { id: "vs.shadededges", label: "Shaded with Edges", radio: true },
      { id: "vs.gray", label: "Shades of Gray", radio: true },
      { id: "vs.sketchy", label: "Sketchy", radio: true },
      { id: "vs.xray", label: "X-Ray", radio: true },
    ] },
    S,
    { id: "view.ucs", label: "New UCS", sub: [
      { id: "ucs.world", label: "World" },
      { id: "ucs.previous", label: "Previous" },
      { id: "ucs.set", label: "Origin" },
      { id: "ucs.manager", label: "Named UCS..." },
    ] },
    { id: "view.display", label: "Display", sub: [
      { id: "view.ucsicon", label: "UCS Icon", toggle: true },
      { id: "view.viewcube", label: "ViewCube", toggle: true },
      { id: "view.navbar", label: "Navigation Bar", toggle: true },
    ] },
    { id: "ui.toolbars", label: "Toolbars..." },
  ] },
  { label: "Insert", items: [
    { id: "block.insert", label: "Block..." },
    { id: "insert.dwgref", label: "DWG Reference..." },
    { id: "insert.attach", label: "Attach..." },
    S,
    { id: "insert.field", label: "Field..." },
    { id: "block.wblock", label: "Write Block..." },
    S,
    { id: "insert.datalink", label: "Data Link..." },
    { id: "insert.dataextract", label: "Data Extraction..." },
  ] },
  { label: "Format", items: [
    { id: "layer.props", label: "Layer..." },
    { id: "layer.states", label: "Layer States Manager..." },
    S,
    { id: "prop.colour", label: "Color..." },
    { id: "prop.linetype", label: "Linetype..." },
    { id: "prop.lineweight", label: "Lineweight..." },
    { id: "prop.transparency", label: "Transparency..." },
    S,
    { id: "anno.style.text", label: "Text Style..." },
    { id: "anno.style.dim", label: "Dimension Style..." },
    { id: "anno.style.table", label: "Table Style..." },
    { id: "anno.style.mleader", label: "Multileader Style..." },
    { id: "draw.pointstyle", label: "Point Style..." },
    S,
    { id: "file.units", label: "Units..." },
    { id: "format.limits", label: "Drawing Limits" },
    S,
    { id: "util.purge", label: "Purge..." },
  ] },
  { label: "Tools", items: [
    { id: "tools.workspaces", label: "Workspaces", sub: WORKSPACES },
    { id: "tools.palettes", label: "Palettes", sub: [
      { id: "prop.palette", label: "Properties", key: "Ctrl+1", toggle: true },
      { id: "pal.toolpalettes", label: "Tool Palettes", key: "Ctrl+3", toggle: true },
      { id: "pal.blocks", label: "Blocks", toggle: true },
      { id: "pal.designcenter", label: "DesignCenter", key: "Ctrl+2", toggle: true },
      { id: "pal.sheetset", label: "Sheet Set Manager", key: "Ctrl+4", toggle: true },
      { id: "pal.xref", label: "External References", toggle: true },
      { id: "pal.history", label: "Drawing History", toggle: true },
    ] },
    { id: "ui.toolbars", label: "Toolbars", sub: [
      { id: "tb.draw", label: "Draw", toggle: true },
      { id: "tb.modify", label: "Modify", toggle: true },
      { id: "tb.osnap", label: "Object Snap", toggle: true },
    ] },
    S,
    { id: "anno.text.spell", label: "Spelling" },
    { id: "util.quickselect", label: "Quick Select..." },
    { id: "util.calc", label: "QuickCalc", key: "Ctrl+8" },
    { id: "util.id", label: "Inquiry", sub: [
      { id: "util.measure.dist", label: "Distance" },
      { id: "util.measure.area", label: "Area" },
      { id: "prop.list", label: "List" },
      { id: "util.id", label: "ID Point" },
    ] },
    S,
    { id: "man.runscript", label: "Run Script..." },
    { id: "man.loadapp", label: "Load Application..." },
    { id: "man.cui", label: "Customize", sub: [
      { id: "man.cui", label: "Interface..." },
      { id: "man.editaliases", label: "Edit Program Parameters (acad.pgp)" },
    ] },
    S,
    // ⚠⚠ THE WAY BACK OUT, and it has to be somewhere a person would look. This shell replaces
    // the studio's header, so the interface switch that used to be a button is gone and Ctrl+K
    // is the only route left — fine once you know it, a trap until you do. AutoCAD keeps its
    // whole configuration behind Tools ▸ Options and the Application menu's Options button, so
    // that is where ours goes, in both places.
    { id: "app.options", label: "Options...", key: "OP" },
  ] },
  { label: "Draw", items: [
    { id: "draw.line", label: "Line" },
    { id: "draw.ray", label: "Ray" },
    { id: "draw.xline", label: "Construction Line" },
    { id: "draw.mline", label: "Multiline" },
    { id: "draw.pline", label: "Polyline" },
    { id: "draw.3dpoly", label: "3D Polyline" },
    { id: "draw.polygon", label: "Polygon" },
    { id: "draw.rect", label: "Rectangle" },
    { id: "draw.helix", label: "Helix" },
    S,
    { id: "draw.arcgroup", label: "Arc", sub: [
      { id: "draw.arc.3p", label: "3 Points" },
      S,
      { id: "draw.arc.sce", label: "Start, Center, End" },
      { id: "draw.arc.sca", label: "Start, Center, Angle" },
      { id: "draw.arc.scl", label: "Start, Center, Length" },
      S,
      { id: "draw.arc.sea", label: "Start, End, Angle" },
      { id: "draw.arc.sed", label: "Start, End, Direction" },
      { id: "draw.arc.ser", label: "Start, End, Radius" },
      S,
      { id: "draw.arc.cse", label: "Center, Start, End" },
      { id: "draw.arc.csa", label: "Center, Start, Angle" },
      { id: "draw.arc.csl", label: "Center, Start, Length" },
      S,
      { id: "draw.arc.cont", label: "Continue" },
    ] },
    { id: "draw.circlegroup", label: "Circle", sub: [
      { id: "draw.circle", label: "Center, Radius" },
      { id: "draw.circle.cd", label: "Center, Diameter" },
      S,
      { id: "draw.circle.2p", label: "2 Points" },
      { id: "draw.circle.3p", label: "3 Points" },
      S,
      { id: "draw.circle.ttr", label: "Tan, Tan, Radius" },
      { id: "draw.circle.ttt", label: "Tan, Tan, Tan" },
    ] },
    { id: "draw.donut", label: "Donut" },
    { id: "draw.splinegroup", label: "Spline", sub: [
      { id: "draw.spline.fit", label: "Fit Points" },
      { id: "draw.spline.cv", label: "Control Vertices" },
    ] },
    { id: "draw.ellipsegroup", label: "Ellipse", sub: [
      { id: "draw.ellipse.center", label: "Center" },
      { id: "draw.ellipse.axis", label: "Axis, End" },
      { id: "draw.ellipse.arc", label: "Arc" },
    ] },
    S,
    { id: "draw.block", label: "Block", sub: [
      { id: "block.create", label: "Make..." },
      { id: "block.setbase", label: "Base" },
      { id: "block.attdef", label: "Define Attributes..." },
    ] },
    { id: "draw.point", label: "Point", sub: [
      { id: "draw.point", label: "Multiple Point" },
      { id: "mod.divide", label: "Divide" },
      { id: "draw.measurepts", label: "Measure" },
    ] },
    S,
    { id: "draw.hatch", label: "Hatch..." },
    { id: "draw.gradient", label: "Gradient..." },
    { id: "draw.boundary", label: "Boundary..." },
    { id: "draw.region", label: "Region" },
    { id: "draw.wipeout", label: "Wipeout" },
    { id: "draw.revcloud", label: "Revision Cloud" },
    S,
    { id: "anno.text.group", label: "Text", sub: [
      { id: "anno.text.multi", label: "Multiline Text..." },
      { id: "anno.text.single", label: "Single Line Text" },
    ] },
  ] },
  { label: "Dimension", items: [
    { id: "anno.dim.quick", label: "Quick Dimension" },
    { id: "anno.dim.linear", label: "Linear" },
    { id: "anno.dim.aligned", label: "Aligned" },
    { id: "anno.dim.arc", label: "Arc Length" },
    { id: "anno.dim.ordinate", label: "Ordinate" },
    { id: "anno.dim.radius", label: "Radius" },
    { id: "anno.dim.jogged", label: "Jogged" },
    { id: "anno.dim.diameter", label: "Diameter" },
    { id: "anno.dim.angular", label: "Angular" },
    S,
    { id: "anno.dim.baseline", label: "Baseline" },
    { id: "anno.dim.continue", label: "Continue" },
    S,
    { id: "anno.leader", label: "Multileader" },
    { id: "anno.centermark", label: "Center Mark" },
    S,
    { id: "anno.dim.break", label: "Dimension Break" },
    { id: "anno.dim.space", label: "Adjust Space" },
    { id: "anno.dim.jogline", label: "Jogged Linear" },
    S,
    { id: "anno.style.dim", label: "Dimension Style..." },
  ] },
  { label: "Modify", items: [
    { id: "prop.palette", label: "Properties", key: "Ctrl+1" },
    { id: "prop.match", label: "Match Properties" },
    { id: "mod.changetospace", label: "Change to ByLayer" },
    S,
    { id: "mod.object", label: "Object", sub: [
      { id: "mod.pedit", label: "Polyline" },
      { id: "mod.splinedit", label: "Spline" },
      { id: "mod.hatchedit", label: "Hatch..." },
      { id: "mod.textedit", label: "Text...", key: "ED" },
    ] },
    { id: "mod.clip", label: "Clip", sub: [
      { id: "insert.clip", label: "Xref" },
      { id: "mod.clip.viewport", label: "Viewport" },
    ] },
    S,
    { id: "mod.erase", label: "Erase" },
    { id: "mod.copy", label: "Copy" },
    { id: "mod.mirror", label: "Mirror" },
    { id: "mod.offset", label: "Offset" },
    { id: "mod.array.rect", label: "Array", sub: [
      { id: "mod.array.rect", label: "Rectangular Array" },
      { id: "mod.array.path", label: "Path Array" },
      { id: "mod.array.polar", label: "Polar Array" },
    ] },
    S,
    { id: "mod.move", label: "Move" },
    { id: "mod.rotate", label: "Rotate" },
    { id: "mod.scale", label: "Scale" },
    { id: "mod.stretch", label: "Stretch" },
    { id: "mod.lengthen", label: "Lengthen" },
    { id: "mod.trim", label: "Trim" },
    { id: "mod.extend", label: "Extend" },
    { id: "mod.break", label: "Break" },
    { id: "mod.join", label: "Join" },
    { id: "mod.chamfer", label: "Chamfer" },
    { id: "mod.fillet", label: "Fillet" },
    { id: "mod.blend", label: "Blend Curves" },
    S,
    // ⚠⚠ **THIS SAID "all three are wired" AND NONE OF THEM WERE.** The ids were `pdvn.union`,
    // `pdvn.subtract` and `pdvn.intersect` — the design app's boolean tools, which operated on
    // that product's flat shapes. Neither the ids nor the claim survived the move here: measured,
    // all three had no handler at all, so the menu offered a wired feature and did nothing.
    // ⇒ AutoCAD's own ids and command names, and grey with a reason (see `WHY`).
    { id: "mod.solidedit", label: "Solid Editing", sub: [
      { id: "mod.union", label: "Union" },
      { id: "mod.subtract", label: "Subtract" },
      { id: "mod.intersect", label: "Intersect" },
    ] },
    S,
    { id: "mod.explode", label: "Explode" },
  ] },
  { label: "Parametric", items: [
    { id: "par.geomgroup", label: "Geometric Constraints", sub: [
      { id: "par.coincident", label: "Coincident" },
      { id: "par.collinear", label: "Collinear" },
      { id: "par.concentric", label: "Concentric" },
      { id: "par.parallel", label: "Parallel" },
      { id: "par.perpendicular", label: "Perpendicular" },
      { id: "par.horizontal", label: "Horizontal" },
      { id: "par.vertical", label: "Vertical" },
      { id: "par.tangent", label: "Tangent" },
      { id: "par.equal", label: "Equal" },
    ] },
    { id: "par.autoconstrain", label: "Auto Constrain" },
    S,
    { id: "par.dimconstraint", label: "Dimensional Constraints", sub: [
      { id: "par.dim.aligned", label: "Aligned" },
      { id: "par.dim.angular", label: "Angular" },
      { id: "par.dim.radial", label: "Radial" },
    ] },
    S,
    { id: "par.paramgr", label: "Parameters Manager..." },
    { id: "par.delcon", label: "Delete Constraints" },
  ] },
  { label: "Window", items: [
    { id: "win.close", label: "Close" },
    { id: "win.closeall", label: "Close All" },
    S,
    { id: "win.cascade", label: "Cascade" },
    { id: "win.tileh", label: "Tile Horizontally" },
    { id: "win.tilev", label: "Tile Vertically" },
    S,
    { id: "ui.lockui", label: "Lock Location", sub: [
      { id: "ui.lock.toolbars", label: "Floating Toolbars/Panels", toggle: true },
      { id: "ui.lock.windows", label: "Floating Windows", toggle: true },
      { id: "ui.lock.all", label: "All", toggle: true },
    ] },
    S,
    { id: "ui.cleanscreen", label: "Clean Screen", key: "Ctrl+0", toggle: true },
  ] },
  { label: "Help", items: [
    { id: "help.help", label: "Help", key: "F1" },
    { id: "help.newfeatures", label: "New Features Workshop" },
    { id: "help.support", label: "Additional Resources" },
    S,
    { id: "help.about", label: "About AutoCAD" },
  ] },
];

// ── the classic dockable toolbars ────────────────────────────────────────────────────────────

/** ⚠⚠ ONLY TOOLBARS THAT ACTUALLY RENDER ARE LISTED. AutoCAD's Tools ▸ Toolbars submenu offers
 *  about forty; listing one here that draws nothing would be the SketchUp lane's exact mistake —
 *  five checkboxes in the mode's own settings that ticked and did nothing. Three real ones. */
export const TOOLBARS: { id: string; label: string; items: (string | null)[]; on: boolean }[] = [
  { id: "tb.draw", label: "Draw", on: false, items: [
    "draw.line", "draw.xline", "draw.pline", "draw.polygon", "draw.rect", null,
    "draw.arc", "draw.circle", "draw.revcloud", "draw.spline.fit", null,
    "draw.ellipse.axis", "draw.ellipse.arc", "block.insert", "block.create", null,
    "draw.point", "draw.hatch", "draw.gradient", "draw.region", null,
    "anno.text.multi", "anno.text.single",
  ] },
  { id: "tb.modify", label: "Modify", on: false, items: [
    "mod.erase", "mod.copy", "mod.mirror", "mod.offset", "mod.array.rect", null,
    "mod.move", "mod.rotate", "mod.scale", "mod.stretch", null,
    "mod.trim", "mod.extend", "mod.break", "mod.breakat", "mod.join", null,
    "mod.chamfer", "mod.fillet", "mod.blend", "mod.explode",
  ] },
  { id: "tb.osnap", label: "Object Snap", on: false, items: [
    "osnap.endpoint", "osnap.midpoint", "osnap.intersection", "osnap.center",
    "osnap.perpendicular", "osnap.nearest", null,
    "osnap.node", "osnap.quadrant", "osnap.tangent", "osnap.insertion",
    "osnap.extension", "osnap.parallel", "osnap.apparent", null,
    "osnap.settings",
  ] },
];

// ── the running object snaps ─────────────────────────────────────────────────────────────────

/** AutoCAD's fourteen running snaps and the studio's own name for each, where there is one.
 *
 * ⚠⚠ **THIRTEEN OF THE FOURTEEN ARE REAL AS OF 2026-08-31, AND ONE OF THEM ALWAYS WAS.** This
 * list used to map six and grey eight, with a header calling itself the cleanest place in the
 * shell to see the greying rule work. It was — but one of the greys was WRONG: `objectSnap` has
 * computed `quadrant` since it was written, `ALL_SNAPS` has always listed it, and the drop-up
 * greyed it anyway. A grey that hides a built feature is the same lie as a live button that does
 * nothing, pointing the other way, and it is the harder one to notice: nobody reports a tool
 * they were told does not exist.
 *
 * ⇒ The other six were then built rather than relabelled — geometric centre (the AREA centroid,
 * not the vertex average), insertion (a block's own origin), extension, tangent, parallel and
 * apparent intersection. See `geometry.ts`; each carries the reasoning for what the word can
 * honestly mean on a single-plane canvas.
 *
 * ⚠⚠ **ALL FOURTEEN ARE LIVE AS OF 2026-08-31.** Node was the last grey, and its note said
 * exactly why: *"AutoCAD's Node catches POINT objects, and POINT is not built"*. That was the
 * right answer — and it also named its own expiry date. POINT was built the same day, so the
 * reason expired and the grey went with it.
 *
 * ⚠ Keep this as the example of why a grey needs a REASON rather than "not built yet". The
 * reason told the next person precisely what would have to happen for the button to come alive,
 * and then it happened. "Not built yet" would have told nobody anything, and nothing about it
 * would ever have changed.
 */
export const OSNAP_MODES: { id: string; label: string; key?: string; snap?: string }[] = [
  { id: "osnap.endpoint", label: "Endpoint", snap: "endpoint" },
  { id: "osnap.midpoint", label: "Midpoint", snap: "midpoint" },
  { id: "osnap.center", label: "Center", snap: "centre" },
  { id: "osnap.geocenter", label: "Geometric Center", snap: "geocentre" },
  { id: "osnap.node", label: "Node", snap: "node" },
  { id: "osnap.quadrant", label: "Quadrant", snap: "quadrant" },
  { id: "osnap.intersection", label: "Intersection", snap: "intersection" },
  { id: "osnap.extension", label: "Extension", snap: "extension" },
  { id: "osnap.insertion", label: "Insertion", snap: "insertion" },
  { id: "osnap.perpendicular", label: "Perpendicular", snap: "perpendicular" },
  { id: "osnap.tangent", label: "Tangent", snap: "tangent" },
  { id: "osnap.nearest", label: "Nearest", snap: "nearest" },
  { id: "osnap.apparent", label: "Apparent Intersection", snap: "apparent" },
  { id: "osnap.parallel", label: "Parallel", snap: "parallel" },
];

// ── the status bar ───────────────────────────────────────────────────────────────────────────

/** The status-bar toggles, left to right, as AutoCAD 2024 orders them. `drop` names a drop-up. */
export const STATUS_ITEMS: { id: string; label: string; key?: string; drop?: string }[] = [
  { id: "st.grid", label: "Grid", key: "F7" },
  { id: "st.snapmode", label: "Snap Mode", key: "F9" },
  { id: "st.infer", label: "Infer Constraints" },
  { id: "st.dynin", label: "Dynamic Input", key: "F12" },
  { id: "st.ortho", label: "Ortho Mode", key: "F8" },
  { id: "st.polar", label: "Polar Tracking", key: "F10", drop: "polarang" },
  { id: "st.iso", label: "Isometric Drafting" },
  { id: "st.otrack", label: "Object Snap Tracking", key: "F11" },
  { id: "st.osnap", label: "2D Object Snap", key: "F3", drop: "osnap" },
  { id: "st.lwt", label: "Show/Hide Lineweight" },
  { id: "st.transparency", label: "Show/Hide Transparency" },
  { id: "st.cycling", label: "Selection Cycling" },
  { id: "st.annovis", label: "Annotation Visibility" },
  { id: "st.autoscale", label: "AutoScale" },
  { id: "st.annoscale", label: "Annotation Scale", drop: "annoscale" },
  { id: "st.workspace", label: "Workspace Switching", drop: "workspace" },
  { id: "st.annomon", label: "Annotation Monitor" },
  { id: "st.units", label: "Units", drop: "units" },
  { id: "st.qp", label: "Quick Properties" },
  { id: "st.lockui", label: "Lock User Interface" },
  { id: "st.isolate", label: "Isolate Objects" },
  { id: "st.gfx", label: "Graphics Performance" },
  { id: "ui.cleanscreen", label: "Clean Screen", key: "Ctrl+0" },
];

// ── the palettes ─────────────────────────────────────────────────────────────────────────────

/** Every palette the shell can dock on the right, in the order AutoCAD lists them.
 *
 * ⚠ `panel` names a panel the studio ALREADY HAS. AutoCAD's Properties palette IS our inspector,
 * its Layer Properties Manager IS our layers panel, its Tool Palettes IS where our blocks live,
 * and DWGHISTORY is our history. A second implementation of any of them would drift in a week,
 * so the palette is a frame round the panel we ship, not a rebuild of it. */
export const PALETTES: { id: string; label: string; panel?: string; open?: boolean }[] = [
  { id: "prop.palette", label: "Properties", panel: "@inspector", open: true },
  { id: "layer.props", label: "Layer Properties Manager", panel: "layers", open: true },
  { id: "pal.toolpalettes", label: "Tool Palettes", panel: "blocks" },
  { id: "pal.blocks", label: "Blocks", panel: "blocks" },
  { id: "pal.history", label: "Drawing History", panel: "history" },
  // ⚠⚠ **SIX DESIGN-APP PALETTES USED TO SIT HERE — Rooms, Stairs, Joinery, Pen Styles, Detail
  // Library, Imported Items — and every one of them opened a box reading "Not built yet."**
  // `Studio.panelBody` answers exactly one name, `layers`; the other panel names have no
  // implementation here and never will, because they are a building model's palettes and this
  // app is AutoCAD's. "Not built yet" promised six features that are not coming, which is a
  // worse lie than a grey button — it has no expiry date. Removed with the PDVN tab they were
  // reachable from.
  // No `panel` ⇒ nothing to show, so `handlers.ts` gives them no run and they are grey.
  { id: "pal.designcenter", label: "DesignCenter" },
  { id: "pal.sheetset", label: "Sheet Set Manager" },
  { id: "pal.xref", label: "External References" },
  { id: "pal.markup", label: "Markup Set Manager" },
  { id: "util.calc", label: "QuickCalc" },
];

// ── the layout tabs ──────────────────────────────────────────────────────────────────────────

/** ⚠⚠ ONLY MODEL IS STATIC — the rest of AutoCAD's layout tabs ARE the drawing's layouts, one
 *  tab per layout, named after it. This listed a hardcoded "Layout1" and "Layout2", which is
 *  what a screenshot of AutoCAD looks like and not what AutoCAD does: a drawing with five
 *  sheets shows five tabs and a drawing with none shows only Model. The composed pages are
 *  fetched and appended in `shell.tsx`. */
export const LAYOUT_TABS: { id: string; label: string }[] = [
  { id: "layout.model", label: "Model" },
];

/** Paper space's own commands. ⚠⚠ **THEY LIVE HERE RATHER THAN IN A RIBBON PANEL BECAUSE THE
 *  TAB BAR IS WHERE A DRAFTER REACHES FOR THEM** — AutoCAD's LAYOUT ▸ New, Rename and Delete are
 *  on the tab's right-click menu and its "+", and MVIEW is typed. Registering them as commands
 *  anyway is what makes the command line find them, which is the other half of how they are
 *  actually used: `MVIEW ⏎` and drag a window. */
export const PAPER_CMDS: AcadBtn[] = [
  { id: "layout.new", label: "New Layout", cmd: "LAYOUT",
    hint: "A new sheet at a real paper size" },
  { id: "layout.rename", label: "Rename Layout", cmd: "LAYOUTRENAME" },
  { id: "layout.delete", label: "Delete Layout", cmd: "LAYOUTDELETE" },
  { id: "layout.mview", label: "Viewport", cmd: "MVIEW", key: "MV",
    hint: "Cut a window in the sheet — drag its two corners" },
  { id: "layout.vpscale", label: "Viewport Scale", cmd: "VPSCALE",
    hint: "1:n for the selected window" },
  // ⚠⚠ Two commands AutoCAD has and pdcad did not, both measured on the drawings stored here:
  // 254 of 1,803 viewports freeze a layer and 311 are display-locked, and every one of those
  // round-tripped perfectly while nothing could read or write it.
  { id: "layout.vplock", label: "Display Locked", cmd: "VPLOCK",
    hint: "Stops a stray zoom changing this sheet's plot scale" },
  { id: "layout.vplayer", label: "VP Freeze", cmd: "VPLAYER",
    hint: "Freeze a layer in THIS window only — the Layers palette has the column" },
];

// ── the command line's dictionary ────────────────────────────────────────────────────────────

/** Every button in the ribbon, the menus and the toolbars, flattened by id.
 *
 * ⚠ Built by walking the SAME structures the screen renders, so a command that exists on screen
 * is typable and a command that is typable exists on screen. A second hand-kept list would be
 * the "implemented flag" mistake wearing a different hat. */
/** Commands that live only in a menu, on the QAT or behind a keystroke, with the names AutoCAD
 *  answers to at its own prompt.
 *
 * ⚠⚠ WITHOUT THIS, HALF THE COMMAND LINE WAS A LIE. A pull-down item carries a label and a
 * shortcut but no command NAME, so `UNDO`, `SAVE`, `CLOSE`, `QUIT`, `OPTIONS` and `UNITS` had no
 * entry in the typed index — and typing `U`, which is the single most-used keystroke in AutoCAD,
 * answered `Unknown command "UNDO"` about a command that is wired and works. The buttons were
 * fine; only the typed route was broken, which is exactly the sort of thing that survives a
 * screenshot review. */
const MENU_ONLY: AcadBtn[] = [
  { id: "edit.undo", label: "Undo", cmd: "UNDO", key: "Ctrl+Z",
    hint: "Steps back through the drawing's own history." },
  { id: "edit.redo", label: "Redo", cmd: "REDO", key: "Ctrl+Y" },
  { id: "file.new", label: "New", cmd: "QNEW", key: "Ctrl+N" },
  { id: "file.open", label: "Open", cmd: "OPEN", key: "Ctrl+O" },
  { id: "file.save", label: "Save", cmd: "QSAVE", key: "Ctrl+S" },
  { id: "file.saveas", label: "Save As", cmd: "SAVEAS", key: "Ctrl+Shift+S" },
  { id: "file.close", label: "Close", cmd: "CLOSE" },
  { id: "file.exit", label: "Exit", cmd: "QUIT", key: "Ctrl+Q" },
  { id: "file.units", label: "Units", cmd: "UNITS", key: "UN" },
  { id: "app.options", label: "Options", cmd: "OPTIONS", key: "OP" },
  { id: "edit.selectall", label: "Select All", key: "Ctrl+A" },
  { id: "cloud.open", label: "Open from Web & Mobile" },
  { id: "cloud.save", label: "Save to Web & Mobile" },
  { id: "edit.cancel", label: "Cancel", key: "Esc" },
  { id: "dwg.current", label: "the drawing you are in" },
  { id: "osnap.settings", label: "Object Snap Settings", cmd: "OSNAP", key: "OS" },
  { id: "mod.textedit", label: "Edit Text", cmd: "TEXTEDIT", key: "ED" },
];

/** Every button in the ribbon, the menus, the toolbars and the QAT, flattened by id — and every
 *  NAME any of them answers to.
 *
 * ⚠ Built by walking the SAME structures the screen renders, so a command that exists on screen
 * is typable and a command that is typable exists on screen. A second hand-kept list would be
 * the "implemented flag" mistake wearing a different hat.
 *
 * ⚠⚠ **A COMMAND CAN HAVE MORE THAN ONE NAME AND ALL OF THEM MUST RESOLVE.** Placing a library
 * block is `INSERT` on the Home tab and `PDFURN` on the add-in tab — one id, one handler, two
 * names. Reading the names back off the DEDUPED map would keep whichever was walked first and
 * silently drop the other, so the pairs are collected during the walk instead. */
const _INDEX = (() => {
  const out: Record<string, AcadBtn> = {};
  const cmds: Record<string, string> = {};
  const add = (b: AcadBtn) => {
    if (b.cmd) cmds[b.cmd.toUpperCase()] ||= b.id;
    // ⚠ First writer wins, so a ribbon entry with a `hint` is not overwritten by the bare copy
    // of itself that appears in a flyout or a second tab.
    if (!out[b.id] || (!out[b.id].hint && b.hint)) out[b.id] = { ...out[b.id], ...b };
    b.flyout?.forEach(add);
  };
  const walkGroups = (gs: AcadGroup[]) => gs.forEach((g) => {
    if ("big" in g) add(g.big);
    else if ("wide" in g) add(g.wide);
    else if ("col" in g) g.col.forEach(add);
  });
  MENU_ONLY.forEach(add);
  TABS.forEach((t) => t.panels.forEach((p) => {
    walkGroups(p.groups);
    if (p.more) walkGroups(p.more);
    if (p.launcher) add(p.launcher);
  }));
  const walkMenu = (items: AcadMenuItem[]) => items.forEach((it) => {
    if (it.id && it.label && !out[it.id]) {
      out[it.id] = { id: it.id, label: it.label.replace(/\.\.\.$/, ""), key: it.key };
    }
    if (it.sub) walkMenu(it.sub);
  });
  MENUS.forEach((m) => walkMenu(m.items));
  walkMenu(APP_MENU);
  walkMenu(QAT_MENU);
  OSNAP_MODES.forEach((o) => { out[o.id] ||= { id: o.id, label: o.label }; });
  PALETTES.forEach((p) => { out[p.id] ||= { id: p.id, label: p.label }; });
  STATUS_ITEMS.forEach((s) => { out[s.id] ||= { id: s.id, label: s.label, key: s.key }; });
  LAYOUT_TABS.forEach((l) => { out[l.id] ||= { id: l.id, label: l.label }; });
  PAPER_CMDS.forEach(add);
  return { out, cmds };
})();

export const BTN_BY_ID: Record<string, AcadBtn> = _INDEX.out;

/** ⚠⚠ **COMMANDS AutoCAD ACCEPTS AT THE PROMPT AND GIVES NO BUTTON.** §1 of `docs/GAP.md` says
 *  the 523-control count undercounts "in one direction that matters" — this is that direction,
 *  made reachable. `SOLID` is the 2D filled quad, in **36.4% of this office's 11,734 drawings**,
 *  and AutoCAD 2024 has no ribbon or menu entry for it at all: it is typed, exactly as it has
 *  been since 1982. A tool that can only be reached from a button we would have to invent is
 *  not the same tool.
 *  ⚠ The id must still exist for the rest of the shell — `MODE_OF` maps it, and the notes/greys
 *  machinery keys off ids — so each one gets a real id and a label, just no button anywhere. */
export const TYPED_ONLY: Record<string, { id: string; label: string }> = {
  SOLID: { id: "draw.solid", label: "2D Solid" },
  // ⚠⚠ **`SELECT` IS WHERE AutoCAD'S SELECTION GRAMMAR LIVES AND IT HAS NEVER HAD A BUTTON.**
  // Fence, WPolygon, CPolygon, Previous and Last are OPTIONS of its "Select objects:" prompt —
  // §1 of `docs/GAP.md` calls the option grammar out by name as mass the 523-control count
  // cannot see. Reaching it needs a command, not a control.
  SELECT: { id: "util.select", label: "Select Objects" },
  // ⚠⚠ **SYSTEM VARIABLES ARE HOW AutoCAD IS ACTUALLY CONFIGURED AND SCRIPTED**, and `SETVAR`
  // is the only way to reach one that has no dialog. §1 of `docs/GAP.md` counts ~1,300 of them
  // as mass the 523-control number cannot see. ⚠ A variable can also be typed as a command on
  // its own — `LTSCALE` at the prompt asks for a new value — which is what `VAR_CMDS` is for.
  SETVAR: { id: "util.setvar", label: "Set Variable" },
  // ⚠⚠ **ELEV — typed-only in AutoCAD too, and the pair it sets is why RECTANG's Elevation and
  // Thickness options exist at all.** They used to be remembered in the browser and nowhere
  // else, so they were forgotten on reload and reached nothing but a rectangle. `$ELEVATION`
  // and `$THICKNESS` are read by every DRAW now, which is what makes a line with a thickness a
  // vertical plane — the way a 2D plan becomes a massing model with no 3D command at all.
  ELEV: { id: "util.elev", label: "Elevation and Thickness" },
};

/** ⚠ Every honoured system variable is also a COMMAND, because in AutoCAD it is: typing
 *  `LTSCALE` at the prompt asks *"Enter new value for LTSCALE <1.0000>:"*. Registering them
 *  here means a drafter's muscle memory works without a second name to learn. */
export const VAR_CMDS: readonly string[] = [
  "LTSCALE", "CELTSCALE", "PSLTSCALE", "CELTYPE", "PDMODE", "PDSIZE", "OSMODE",
  "ORTHOMODE", "SNAPMODE", "GRIDMODE", "ANGDIR", "ANGBASE", "INSUNITS", "LUNITS",
  "LUPREC", "AUNITS", "AUPREC", "DIMSCALE", "TEXTSIZE", "CLAYER", "FILLMODE",
  // ⚠⚠ **THIS LIST IS A SECOND SOURCE OF TRUTH AND IT DRIFTED THE DAY IT WAS TESTED.**
  // `UCSFOLLOW` was added to the backend's `SYSVARS`, honoured by the canvas, and settable
  // through the API — and typing it at the command line said *Unknown command*, because
  // nothing added it here. A variable the app claims to honour and nobody can set is the same
  // defect as a lit button that does nothing.
  // ⇒ `SysvarCommandsTests` now fails if the two lists disagree, in either direction.
  "UCSFOLLOW",
  // ⚠ Read by CENTERMARK. Its SIGN chooses the shape — see `SYSVARS` — so a drafter setting it
  // at the prompt needs the description this list's command form prints.
  "DIMCEN",
  // ⚠ AutoCAD's pen weights. LWDISPLAY is the status bar's Show/Hide Lineweight — typing it
  // moves the button and the canvas together; LWDEFAULT is the width a lineweight of "Default"
  // draws as, which `display` resolves while building.
  "LWDISPLAY", "LWDEFAULT",
  // ⚠ Read by MIRROR. 0 — AutoCAD's default — mirrors only where the text SITS and leaves it
  // readable; 1 mirrors the glyphs with everything else. This app did 1 unconditionally.
  "MIRRTEXT",
  // ⚠ Read by FILLET and CHAMFER as the size to use when the command is not given one. 0 is a
  // legal answer to both and means a sharp corner, which is why the dispatch tests `is None`.
  "FILLETRAD", "CHAMFERA",
  // ⚠ Read by `opens_in` when the drawing is opened, not by a command — but still typeable,
  // because a drafter who wants a file to come back on its sheet has to be able to set it.
  "TILEMODE",
  // ⚠ Read by `do_draw` as the DEFAULT for a new object. The client still wins when it names a
  // layer or a colour; these are what it gets when it does not.
  "CECOLOR", "CELWEIGHT",
  // ⚠ Read by `display.build`, so setting it re-renders — the display cache is keyed on the
  // drawing's `updated_at` and SETVAR touches it.
  "ATTMODE",
  // ⚠ Read by `do_draw` for TEXT and MTEXT, and only when the drawing DEFINES the style.
  "TEXTSTYLE",
  // ⚠ Read at the point of USE — DIMSTYLE by the dimension commands, PSVPSCALE by MVIEW.
  "DIMSTYLE", "PSVPSCALE",

  // ── the drafter's own, kept in the PROFILE rather than in the drawing ─────────────────────
  // ⚠⚠ **THESE EIGHT DO NOT TRAVEL WITH THE FILE**, and that is AutoCAD's arrangement too: a
  // pick box is how big one drafter likes their target, not part of the client's building. The
  // server keeps them in `models.UserVar` and routes them away from the undo stack — see
  // `sysvars.USER_SCOPED`. Typing the name is still how they are set, which is why they are
  // here: the whole point of this list is that a drafter's muscle memory works.
  "PICKBOX", "APERTURE", "POLARANG", "ZOOMFACTOR", "GRIPSIZE",
  "PICKADD", "PICKFIRST", "HIGHLIGHT",

  // ── what a command remembers between uses ────────────────────────────────────────────────
  // ⚠ Read by CHAMFER as the distance off the SECOND line picked. `CHAMFERA` above is the
  // first; passing one for both made every chamfer symmetric, so a 30x10 could not be drawn.
  "CHAMFERB",
  // ⚠ Read and WRITTEN by OFFSET, HATCH and PLINE — AutoCAD's prompts read `<100.0000>` and
  // `Current line-width is 0.0000` because the drawing remembers the last answer.
  "OFFSETDIST", "PLINEWID", "HPNAME", "HPSCALE", "HPANG",
  // ⚠ Read by the canvas as the step F9 rounds to. A DRAWING variable, unlike the eight above:
  // the grid a detail is drawn on belongs to the detail.
  "SNAPUNIT",

  // ── the current UCS, in the drawing since 2026-09-09 ─────────────────────────────────────
  // ⚠⚠ The working origin used to live in `localStorage`; AutoCAD keeps it in the file header
  // and the owner ruled that the bar is AutoCAD's behaviour. Typing `UCSORG` now answers with
  // the origin the drawing carries, as it does in AutoCAD — the UCS command is still how a
  // drafter usually sets it, by picking.
  "UCSORG", "UCSXDIR", "UCSYDIR",
  // ⚠ ELEV sets both; typing either name on its own is AutoCAD's other way in.
  "ELEVATION", "THICKNESS",
];
for (const v of VAR_CMDS) TYPED_ONLY[v] = { id: `util.var.${v.toLowerCase()}`, label: v };
for (const [cmd, b] of Object.entries(TYPED_ONLY)) {
  _INDEX.out[b.id] ||= { id: b.id, label: b.label, cmd };
  _INDEX.cmds[cmd] = b.id;
}

/** Command name → id, for everything with a `cmd`. */
export const ID_BY_CMD: Record<string, string> = _INDEX.cmds;

/** acad.pgp — AutoCAD's own command aliases, which is what a drafter's fingers actually type.
 *
 * ⚠ These are Autodesk's, not ours. `L`, `M`, `CO`, `TR` and `O` are the muscle memory the whole
 * mode exists to honour; inventing a shorter one for a command AutoCAD leaves long would make
 * this a third dialect rather than the one they already know. */
export const ALIASES: Record<string, string> = {
  L: "LINE", PL: "PLINE", C: "CIRCLE", A: "ARC", REC: "RECTANG", RECTANGLE: "RECTANG",
  POL: "POLYGON", EL: "ELLIPSE", H: "HATCH", BH: "HATCH", SPL: "SPLINE", XL: "XLINE",
  ML: "MLINE", DO: "DONUT", PO: "POINT", REG: "REGION", BO: "BOUNDARY", GD: "GRADIENT",
  REVCLOUD: "REVCLOUD", RC: "REVCLOUD",
  M: "MOVE", CO: "COPY", CP: "COPY", RO: "ROTATE", SC: "SCALE", MI: "MIRROR",
  O: "OFFSET", AR: "ARRAYRECT", ARRAY: "ARRAYRECT", TR: "TRIM", EX: "EXTEND",
  F: "FILLET", CHA: "CHAMFER", X: "EXPLODE", J: "JOIN", BR: "BREAK", AL: "ALIGN",
  S: "STRETCH", LEN: "LENGTHEN", DIV: "DIVIDE", E: "ERASE", DEL: "ERASE",
  U: "UNDO", MA: "MATCHPROP", LI: "LIST", PU: "PURGE", G: "GROUP",
  T: "MTEXT", MT: "MTEXT", DT: "TEXT", ED: "TEXTEDIT", VS: "VISUALSTYLES", "3DO": "3DORBIT",
  D: "DIMSTYLE", DLI: "DIMLINEAR", DIMLIN: "DIMLINEAR", DAL: "DIMALIGNED",
  DAN: "DIMANGULAR", DRA: "DIMRADIUS", DDI: "DIMDIAMETER", DCO: "DIMCONTINUE",
  DBA: "DIMBASELINE", MLD: "MLEADER", MLE: "MLEADER", LE: "MLEADER",
  LA: "LAYER", LAYER: "LAYER", CH: "PROPERTIES", PR: "PROPERTIES", MO: "PROPERTIES",
  I: "INSERT", B: "BLOCK", BE: "BEDIT", W: "WBLOCK", ATT: "ATTDEF",
  Z: "ZOOM", P: "PAN", RE: "REGEN", V: "VIEW", DI: "DIST", AA: "AREA",
  UNI: "UNION", SU: "SUBTRACT", IN: "INTERSECT",
  OP: "OPTIONS", PLOT: "PLOT", PRINT: "PLOT", SAVE: "QSAVE", SAVEAS: "SAVEAS",
  TP: "TOOLPALETTES", ADC: "ADCENTER", SSM: "SHEETSET", QC: "QUICKCALC",
  // The add-in's own, named the way a plug-in names its commands — a prefix, never a collision.
  PDW: "PDWALL", PDD: "PDDOOR", PDN: "PDWINDOW", PDS: "PDSTAIR", PDF: "PDFURN",
};

/** What the command line answers to. Aliases first, then the real names. */
export function resolveCommand(raw: string): { id?: string; cmd: string } {
  const t = raw.trim().toUpperCase().replace(/^[_-]+/, "");   // `_LINE` and `-LAYER` both work
  if (!t) return { cmd: t };
  const cmd = ALIASES[t] || t;
  return { id: ID_BY_CMD[cmd], cmd };
}

/** Every typable command name, for the AutoComplete drop-up. */
export const ALL_COMMANDS: string[] = (() => {
  const s = new Set<string>();
  Object.values(BTN_BY_ID).forEach((b) => { if (b.cmd) s.add(b.cmd.toUpperCase()); });
  Object.keys(ALIASES).forEach((a) => s.add(a));
  return [...s].sort();
})();
